import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  FaChartBar,
  FaChartPie,
  FaThermometerThreeQuarters,
  FaTimes,
  FaArrowLeft,
  FaChevronDown
} from 'react-icons/fa';
import { FiChevronRight } from 'react-icons/fi';

import { HiChevronDoubleLeft, HiChevronLeft, HiChevronRight, HiChevronDoubleRight } from 'react-icons/hi';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
  ReferenceDot,
  ScatterChart,
  Scatter,
  ZAxis,
  Legend,
  Rectangle,
  ComposedChart
} from 'recharts';
import { fetchProjectDetails, fetchTraces } from '../services/api';

// Utility function to safely convert values to numbers
const safeNumber = (value, defaultValue = 0) => {
  if (value === null || value === undefined) return defaultValue;
  const num = parseFloat(value);
  return isNaN(num) ? defaultValue : num;
};

// Utility function to safely format numbers with toFixed
const safeToFixed = (value, decimals = 2, defaultValue = '0') => {
  try {
    const num = safeNumber(value);
    return num.toFixed(decimals);
  } catch (error) {
    console.warn('Error formatting number:', error);
    return defaultValue;
  }
};



const ProjectDetails = () => {
  const { projectId } = useParams();
  const [activeTab, setActiveTab] = useState('Token Usage');
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [runData, setRunData] = useState(null);
  const [selectedRun, setSelectedRun] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [runsPerPage] = useState(5);
  const [error, setError] = useState(null);
  // Remove config state  
  // Remove any default date filter, always show all runs for the project
  useEffect(() => {
    // Fetch real data from MongoDB
    const loadRealData = async () => {
      setLoading(true);
      try {
        console.log('Fetching project details for ID:', projectId);
        const projectDetails = await fetchProjectDetails(projectId);
        console.log('Raw project details response:', projectDetails);
        console.log('Response type:', typeof projectDetails);
        console.log('Response keys:', Object.keys(projectDetails || {}));

        // Extract data from the response - API returns data directly
        const projectData = projectDetails;
        console.log('Extracted project data:', projectData);

        // Check if we actually got project data
        if (!projectData || !projectData.id) {
          console.error('Project data validation failed:', {
            hasProjectData: !!projectData,
            projectId: projectData?.id,
            allKeys: Object.keys(projectData || {})
          });
          throw new Error('Project not found in response');
        }

        // Set project data
        setProject(projectData);

        // Set runs data with validation
        // Handle both cases: when projectData.runs is an array directly or a property
        const runs = Array.isArray(projectData.runs) ? projectData.runs : (projectData.runs || []);
        console.log(`Found ${runs.length} runs in project data`);

        // Validate and clean run data
        const validatedRuns = runs.map((run, index) => {
          try {
            // Ensure numeric fields are properly converted
            if (run.execution_duration) {
              run.execution_duration = safeNumber(run.execution_duration);
            }
            if (run.llm_calls) {
              run.llm_calls = run.llm_calls.map(call => ({
                ...call,
                cost: safeNumber(call.cost),
                total_tokens: safeNumber(call.total_tokens),
                input_tokens: safeNumber(call.input_tokens),
                output_tokens: safeNumber(call.output_tokens),
                latency_ms: safeNumber(call.latency_ms)
              }));
            }
            return run;
          } catch (error) {
            console.warn(`Error validating run ${index}:`, error);
            return run; // Return original run if validation fails
          }
        });

        setRunData({ runs: validatedRuns });

        // Debug: Log the data structure
        console.log('Project Details:', projectDetails);
        console.log('Project Data:', projectData);
        console.log('Token Usage:', projectData.token_usage);
        console.log('Costs:', projectData.costs);
        console.log('Validated Runs:', validatedRuns.length);

        // Debug: Log OCR metrics data
        if (validatedRuns.length > 0) {
          console.log('Runs with OCR metrics:');
          validatedRuns.forEach((run, index) => {
            console.log(`Run ${index + 1} (${run.run_id}):`, {
              hasOcrMetrics: !!run.ocr_metrics,
              ocrMetricsCount: run.ocr_metrics?.length || 0,
              ocrMetrics: run.ocr_metrics,
              executionDuration: run.execution_duration,
              llmCallsCount: run.llm_calls?.length || 0
            });
          });
        }
      } catch (err) {
        console.error('Project details loading error:', err);
        console.error('Error details:', {
          message: err.message,
          stack: err.stack,
          projectId: projectId,
          response: ProjectDetails
        });
        setError(`Failed to load project details: ${err.message}`);
        setProject(null);
      } finally {
        setLoading(false);
      }
    };
    loadRealData();
  }, [projectId]);



  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  // Handle click on status cell to show run details
  const handleStatusClick = (run) => {
    setSelectedRun(run);
    setShowModal(true);
  };

  // Close modal
  const closeModal = () => {
    setShowModal(false);
    setSelectedRun(null);
  };

  // Get current runs for pagination
  const indexOfLastRun = currentPage * runsPerPage;
  const indexOfFirstRun = indexOfLastRun - runsPerPage;

  // Use only real runs data from database and sort by start_time (latest first)
  const allRuns = (runData?.runs || []).sort((a, b) => {
    const dateA = new Date(a.start_time);
    const dateB = new Date(b.start_time);
    return dateB - dateA; // Sort in descending order (newest first)
  });

  const currentRuns = allRuns.slice(indexOfFirstRun, indexOfLastRun);
  const totalPages = Math.ceil(allRuns.length / runsPerPage);

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const nextPage = () => setCurrentPage(prev => Math.min(prev + 1, totalPages));
  const prevPage = () => setCurrentPage(prev => Math.max(prev - 1, 1));
  const firstPage = () => setCurrentPage(1);
  const lastPage = () => setCurrentPage(totalPages);

  if (loading) {
    return <div className="flex justify-center items-center h-64">Loading project details...</div>;
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <img src="/images/svg/error-icon.svg" alt="Error" className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error loading project details</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error}</p>
                <p className="mt-1">Project ID: {projectId}</p>
              </div>
            </div>
          </div>
        </div>
        <Link to="/projects" className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700">
          ← Back to Projects
        </Link>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="space-y-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <img src="/images/svg/warning-icon.svg" alt="Warning" className="h-5 w-5 text-yellow-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">Project not found</h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p>The project with ID "{projectId}" could not be found.</p>
              </div>
            </div>
          </div>
        </div>
        <Link to="/projects" className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700">
          ← Back to Projects
        </Link>
      </div>
    );
  }

  // Wrap the main render in error boundary
  try {

    const modelBadgeColor = (model) => {
      const modelLower = model.toLowerCase();
      switch (modelLower) {
        case 'gpt-4':
        case 'gpt-4o':
          return 'bg-blue-100 text-blue-600';
        case 'gpt-4o-large':
          return 'bg-indigo-100 text-indigo-600';
        case 'gpt-4o-mini':
          return 'bg-cyan-100 text-cyan-600';
        case 'gpt-3.5-turbo':
          return 'bg-green-100 text-green-600';
        case 'claude':
        case 'claude-3':
          return 'bg-purple-100 text-purple-600';
        case 'llama-3':
          return 'bg-amber-100 text-amber-600';
        case 'mistral-7b':
          return 'bg-red-100 text-red-600';
        case 'custom llm-x':
          return 'bg-orange-100 text-orange-600';
        default:
          return 'bg-gray-100 text-gray-600';
      }
    };

    // TokenUsageChart component
    const TokenUsageChart = () => {
      // Generate enhanced data from real runs with more granular information
      const data = [];
      const modelBreakdown = {};
      const hourlyData = [];

      if (allRuns.length > 0) {
        // Group runs by date and sum tokens from ALL LLM calls
        const dateMap = {};
        const hourlyMap = {};

        allRuns.forEach(run => {
          if (run.start_time) {
            const date = new Date(run.start_time).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
            const hour = new Date(run.start_time).getHours();
            const tokens = run.llm_calls?.reduce((sum, call) => sum + (call.total_tokens || 0), 0) || 0;

            // Date aggregation
            dateMap[date] = (dateMap[date] || 0) + tokens;

            // Hourly aggregation
            hourlyMap[hour] = (hourlyMap[hour] || 0) + tokens;

            // Model breakdown
            run.llm_calls?.forEach(call => {
              const model = call.model || 'Unknown';
              modelBreakdown[model] = (modelBreakdown[model] || 0) + (call.total_tokens || 0);
            });
          }
        });

        // Convert to array format for chart
        Object.entries(dateMap).forEach(([date, tokens]) => {
          data.push({ date, tokens, formattedTokens: tokens.toLocaleString() });
        });

        // Convert hourly data
        Object.entries(hourlyMap).forEach(([hour, tokens]) => {
          hourlyData.push({ hour: parseInt(hour), tokens });
        });

        // Sort by date
        data.sort((a, b) => new Date(a.date + ', 2024') - new Date(b.date + ', 2024'));
        hourlyData.sort((a, b) => a.hour - b.hour);
      }



      const formatYAxis = (value) => {
        if (value >= 1000000) {
          return `${(value / 1000000).toFixed(1)}M`;
        } else if (value >= 1000) {
          return `${(value / 1000).toFixed(0)}k`;
        }
        return value.toString();
      };

      const formatCurrency = (value) => {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 4,
          maximumFractionDigits: 4
        }).format(value);
      };

      const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
          const value = payload[0].value;
          const estimatedCost = (value / 1000) * 0.002; // Cost per 1k tokens

          return (
            <div className="bg-white p-6 shadow-2xl rounded-2xl border-0 backdrop-blur-sm bg-opacity-98">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center">
                  <div className="w-3 h-3 rounded-full bg-gradient-to-r from-blue-500 to-indigo-600 mr-2"></div>
                  <p className="font-bold text-lg text-gray-900 font-inter">{label}</p>
                </div>
                <div className="w-2 h-2 rounded-full bg-gradient-to-r from-emerald-500 to-teal-600 animate-pulse"></div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 font-inter">Token Usage:</span>
                  <span className="text-xl font-bold text-gray-900 font-inter">
                    {value.toLocaleString()} tokens
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600 font-inter">Estimated Cost:</span>
                  <span className="text-lg font-semibold text-emerald-600 font-inter">
                    {formatCurrency(estimatedCost)}
                  </span>
                </div>

                <div className="w-full bg-gray-100 rounded-full h-2">
                  <div
                    className="bg-gradient-to-r from-blue-500 to-indigo-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${Math.min((value / Math.max(...data.map(d => d.tokens))) * 100, 100)}%` }}
                  ></div>
                </div>
              </div>
            </div>
          );
        }
        return null;
      };

      const totalTokens = data.reduce((sum, item) => sum + item.tokens, 0);
      const avgTokens = data.length > 0 ? totalTokens / data.length : 0;
      const maxTokens = Math.max(...data.map(d => d.tokens), 1);
      const estimatedTotalCost = (totalTokens / 1000) * 0.002;

      // Calculate trends
      const trend = data.length > 1 ?
        ((data[data.length - 1].tokens - data[data.length - 2].tokens) / data[data.length - 2].tokens * 100) : 0;

      return (
        <div className="relative">
          {/* Premium Header with Stats */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 mb-6 border border-blue-100">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-2xl font-bold text-gray-900 font-inter mb-2">Token Usage Analytics</h3>
                <p className="text-sm text-gray-600 font-inter">Comprehensive AI model consumption tracking</p>
              </div>
              <div className="flex items-center space-x-4">
                <div className="text-center">
                  <div className="text-3xl font-bold text-gray-900 font-inter">
                    {formatYAxis(totalTokens)}
                  </div>
                  <div className="text-xs text-gray-500 font-inter">Total Tokens</div>
                </div>
                <div className="w-px h-12 bg-gray-300"></div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-emerald-600 font-inter">
                    {formatCurrency(estimatedTotalCost)}
                  </div>
                  <div className="text-xs text-gray-500 font-inter">Estimated Cost</div>
                </div>
              </div>
            </div>

            {/* Quick Stats Row */}
            <div className="grid grid-cols-4 gap-4">
              <div className="bg-white rounded-xl p-3 border border-blue-100">
                <div className="text-sm text-gray-600 font-inter">Daily Avg</div>
                <div className="text-lg font-bold text-gray-900 font-inter">{formatYAxis(avgTokens)}</div>
              </div>
              <div className="bg-white rounded-xl p-3 border border-blue-100">
                <div className="text-sm text-gray-600 font-inter">Peak Usage</div>
                <div className="text-lg font-bold text-gray-900 font-inter">{formatYAxis(maxTokens)}</div>
              </div>
              <div className="bg-white rounded-xl p-3 border border-blue-100">
                <div className="text-sm text-gray-600 font-inter">Days Tracked</div>
                <div className="text-lg font-bold text-gray-900 font-inter">{data.length}</div>
              </div>
              <div className="bg-white rounded-xl p-3 border border-blue-100">
                <div className="text-sm text-gray-600 font-inter">Trend</div>
                <div className={`text-lg font-bold font-inter ${trend > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {trend > 0 ? '+' : ''}{trend.toFixed(1)}%
                </div>
              </div>
            </div>
          </div>

          {/* Enhanced Chart Container */}
          <div className="bg-gradient-to-br from-white via-blue-50 to-indigo-50 rounded-3xl p-8 border border-blue-100 shadow-lg relative overflow-hidden">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-5">
              <div className="absolute inset-0" style={{
                backgroundImage: `radial-gradient(circle at 25% 25%, #4F46E5 0%, transparent 50%),
                                radial-gradient(circle at 75% 75%, #6366F1 0%, transparent 50%)`,
                backgroundSize: '200px 200px'
              }}></div>
            </div>

            <div className="h-96 relative z-10">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart
                  data={data}
                  margin={{ top: 40, right: 40, left: 30, bottom: 50 }}
                >
                  <defs>
                    <linearGradient id="lineGradient" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor="#4F46E5" />
                      <stop offset="50%" stopColor="#6366F1" />
                      <stop offset="100%" stopColor="#8B5CF6" />
                    </linearGradient>
                    <filter id="glow">
                      <feGaussianBlur stdDeviation="3" result="coloredBlur" />
                      <feMerge>
                        <feMergeNode in="coloredBlur" />
                        <feMergeNode in="SourceGraphic" />
                      </feMerge>
                    </filter>
                  </defs>

                  <CartesianGrid
                    strokeDasharray="12 12"
                    vertical={false}
                    stroke="#E5E7EB"
                    strokeOpacity={0.2}
                  />

                  <XAxis
                    dataKey="date"
                    axisLine={false}
                    tickLine={false}
                    tick={{
                      fontSize: 13,
                      fill: '#4B5563',
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 600
                    }}
                    dy={20}
                    tickMargin={15}
                  />

                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{
                      fontSize: 12,
                      fill: '#4B5563',
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 600
                    }}
                    domain={[0, 'dataMax + dataMax * 0.15']}
                    tickFormatter={formatYAxis}
                    tickCount={8}
                    dx={-20}
                    tickMargin={15}
                  />

                  <Tooltip content={<CustomTooltip />} />

                  {/* Main line with gradient and glow effect */}
                  <Line
                    type="monotone"
                    dataKey="tokens"
                    stroke="url(#lineGradient)"
                    strokeWidth={4}
                    filter="url(#glow)"
                    dot={{
                      fill: '#4F46E5',
                      stroke: '#ffffff',
                      strokeWidth: 3,
                      r: 6,
                      filter: "url(#glow)"
                    }}
                    activeDot={{
                      fill: '#4338CA',
                      stroke: '#ffffff',
                      strokeWidth: 4,
                      r: 8,
                      filter: "url(#glow)"
                    }}
                    className="hover:stroke-2 transition-all duration-300"
                  />

                  {/* Enhanced data points with glow */}
                  {data.map((entry, index) => (
                    <ReferenceDot
                      key={`dot-${index}`}
                      x={entry.date}
                      y={entry.tokens}
                      r={0}
                      fill="transparent"
                    />
                  ))}
                </ComposedChart>
              </ResponsiveContainer>
            </div>

            {/* Enhanced Stats Footer */}
            <div className="flex items-center justify-between mt-8 pt-8 border-t border-blue-100">
              <div className="flex items-center space-x-8">
                <div className="flex items-center">
                  <div className="w-4 h-4 rounded-full bg-gradient-to-r from-blue-500 to-indigo-600 mr-3 shadow-sm"></div>
                  <span className="text-sm font-semibold text-gray-700 font-inter">Daily Usage</span>
                </div>
                <div className="flex items-center">
                  <div className="w-4 h-4 rounded-full bg-gradient-to-r from-emerald-500 to-teal-600 mr-3 shadow-sm"></div>
                  <span className="text-sm font-semibold text-gray-700 font-inter">Cost Estimate</span>
                </div>
                <div className="flex items-center">
                  <div className="w-4 h-4 rounded-full bg-gradient-to-r from-purple-500 to-pink-600 mr-3 shadow-sm"></div>
                  <span className="text-sm font-semibold text-gray-700 font-inter">Peak Performance</span>
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm font-bold text-gray-900 font-inter">
                  Efficiency: {((avgTokens / maxTokens) * 100).toFixed(1)}%
                </div>
                <div className="text-xs text-gray-500 font-inter">
                  {data.length} day{data.length !== 1 ? 's' : ''} of data • {Object.keys(modelBreakdown).length} models
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    };

    // LatencyChart component
    const LatencyChart = () => {
      // Generate data from real runs
      const data = [];
      if (allRuns.length > 0) {
        // Group runs by date and calculate average latency across ALL LLM calls
        const dateMap = {};
        allRuns.forEach(run => {
          if (run.start_time && run.llm_calls?.length > 0) {
            const date = new Date(run.start_time).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

            // Calculate average latency for all LLM calls in this run
            const latencies = run.llm_calls
              .filter(call => call.latency_ms)
              .map(call => call.latency_ms);

            if (latencies.length > 0) {
              const avgLatency = latencies.reduce((sum, lat) => sum + lat, 0) / latencies.length;
              if (!dateMap[date]) {
                dateMap[date] = { total: 0, count: 0 };
              }
              dateMap[date].total += avgLatency;
              dateMap[date].count += 1;
            }
          }
        });

        // Convert to array format for chart
        Object.entries(dateMap).forEach(([date, { total, count }]) => {
          data.push({ date, latency: Math.round(total / count) });
        });

        // Sort by date
        data.sort((a, b) => new Date(a.date + ', 2024') - new Date(b.date + ', 2024'));
      }

      const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
          return (
            <div className="bg-white p-3 shadow-md rounded-md border border-gray-200">
              <p className="font-medium text-sm font-inter">{label}</p>
              <div className="flex items-center mt-1">
                <div
                  className="w-3 h-3 rounded-sm mr-1.5"
                  style={{ backgroundColor: '#4F46E5' }}
                />
                <span className="text-xs text-gray-700 font-inter">
                  {payload[0].value} ms
                </span>
              </div>
            </div>
          );
        }
        return null;
      };

      return (
        <div className="h-64 relative">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={data}
              margin={{ top: 20, right: 10, left: 0, bottom: 25 }}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#EEF2F6" />
              <XAxis
                dataKey="date"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 10, fill: '#94A3B8', fontFamily: 'Inter, sans-serif' }}
                dy={10}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 10, fill: '#94A3B8', fontFamily: 'Inter, sans-serif' }}
                domain={[500, 1000]}
                dx={-10}
              />
              <Tooltip content={<CustomTooltip />} />
              <Line
                type="monotone"
                dataKey="latency"
                stroke="#4F46E5"
                strokeWidth={2}
                dot={{ r: 3, fill: '#4F46E5' }}
                activeDot={{ r: 5, fill: '#4F46E5' }}
              />
            </LineChart>
          </ResponsiveContainer>
          <div className="text-xs text-gray-500 text-center absolute bottom-[-20px] w-full">
            Average latency: {data.length > 0 ? Math.round(data.reduce((sum, item) => sum + item.latency, 0) / data.length) : 0} ms
          </div>
        </div>
      );
    };

    // ErrorRateChart component
    const ErrorRateChart = () => {
      // Generate data from real runs
      const data = [];
      if (allRuns.length > 0) {
        // Group runs by date and calculate error rate
        const dateMap = {};
        allRuns.forEach(run => {
          if (run.start_time) {
            const date = new Date(run.start_time).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
            if (!dateMap[date]) {
              dateMap[date] = { total: 0, errors: 0 };
            }
            dateMap[date].total += 1;
            if (run.status !== 'success') {
              dateMap[date].errors += 1;
            }
          }
        });

        // Convert to array format for chart
        Object.entries(dateMap).forEach(([date, { total, errors }]) => {
          data.push({ date, errorRate: total > 0 ? errors / total : 0 });
        });

        // Sort by date
        data.sort((a, b) => new Date(a.date + ', 2024') - new Date(b.date + ', 2024'));
      }

      const formatYAxis = (value) => {
        return `${(value * 100).toFixed(0)}%`;
      };

      const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
          return (
            <div className="bg-white p-3 shadow-md rounded-md border border-gray-200">
              <p className="font-medium text-sm font-inter">{label}</p>
              <div className="flex items-center mt-1">
                <div
                  className="w-3 h-3 rounded-sm mr-1.5"
                  style={{ backgroundColor: '#4F46E5' }}
                />
                <span className="text-xs text-gray-700 font-inter">
                  {(payload[0].value * 100).toFixed(1)}% error rate
                </span>
              </div>
            </div>
          );
        }
        return null;
      };

      return (
        <div className="h-64 relative">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={data}
              margin={{ top: 20, right: 10, left: 0, bottom: 25 }}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#EEF2F6" />
              <XAxis
                dataKey="date"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 10, fill: '#94A3B8', fontFamily: 'Inter, sans-serif' }}
                dy={10}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 10, fill: '#94A3B8', fontFamily: 'Inter, sans-serif' }}
                domain={[0, 1]}
                tickFormatter={formatYAxis}
                dx={-10}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar
                dataKey="errorRate"
                fill="#FEE2E2"
                barSize={26}
                radius={[4, 4, 0, 0]}
              >
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.errorRate > 0.5 ? '#EF4444' : '#FEE2E2'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <div className="text-xs text-gray-500 text-center absolute bottom-[-20px] w-full">
            Average error rate (last 7days): 0.38%
          </div>
        </div>
      );
    };

    // Daily Cost Breakdown Chart
    const DailyCostBreakdownChart = () => {
      // Generate data from real runs
      const dailyData = [];
      if (allRuns.length > 0) {
        // Group runs by date and sum tokens/costs from ALL LLM calls
        const dateMap = {};
        allRuns.forEach(run => {
          if (run.start_time) {
            const date = new Date(run.start_time).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
            const tokens = run.llm_calls?.reduce((sum, call) => sum + (call.total_tokens || 0), 0) || 0;
            const cost = run.llm_calls?.reduce((sum, call) => sum + (call.cost || 0), 0) || 0;

            if (!dateMap[date]) {
              dateMap[date] = { tokens: 0, cost: 0 };
            }
            dateMap[date].tokens += tokens;
            dateMap[date].cost += cost;
          }
        });

        // Convert to array format for chart
        Object.entries(dateMap).forEach(([date, { tokens, cost }]) => {
          dailyData.push({
            date,
            tokens,
            cost: parseFloat(cost.toFixed(5))
          });
        });

        // Sort by date
        dailyData.sort((a, b) => new Date(a.date + ', 2024') - new Date(b.date + ', 2024'));
      }

      const formatYAxis = (value) => {
        if (value >= 1000) {
          return `${(value / 1000).toFixed(0)}k`;
        }
        return value.toString();
      };

      const formatCost = (value) => {
        return `$${value.toFixed(5)}`;
      };

      const CustomTooltip = ({ active, payload, label }) => {
        if (active && payload && payload.length) {
          return (
            <div className="bg-white p-3 shadow-md rounded-md border border-gray-200">
              <p className="font-medium text-sm font-inter">{label}</p>
              <div className="flex items-center mt-1">
                <div
                  className="w-3 h-3 rounded-sm mr-1.5"
                  style={{ backgroundColor: '#4F46E5' }}
                />
                <span className="text-xs text-gray-700 font-inter">
                  {payload[0].value} tokens
                </span>
              </div>
              <div className="flex items-center mt-1">
                <div
                  className="w-3 h-3 rounded-sm mr-1.5"
                  style={{ backgroundColor: '#10B981' }}
                />
                <span className="text-xs text-gray-700 font-inter">
                  ${payload[1].value.toFixed(5)} cost
                </span>
              </div>
            </div>
          );
        }
        return null;
      };

      return (
        <div className="h-64 relative">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={dailyData}
              margin={{ top: 20, right: 30, left: 0, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#EEF2F6" />
              <XAxis
                dataKey="date"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 10, fill: '#94A3B8', fontFamily: 'Inter, sans-serif' }}
              />
              <YAxis
                yAxisId="left"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 10, fill: '#94A3B8', fontFamily: 'Inter, sans-serif' }}
                domain={[0, 'dataMax']}
                tickFormatter={formatYAxis}
                tickCount={6}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 10, fill: '#94A3B8', fontFamily: 'Inter, sans-serif' }}
                tickFormatter={formatCost}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar
                yAxisId="left"
                dataKey="tokens"
                fill="#E0EDFF"
                barSize={20}
                radius={[4, 4, 0, 0]}
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="cost"
                stroke="#10B981"
                strokeWidth={2}
                dot={{ r: 3, fill: '#10B981', stroke: '#10B981' }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      );
    };

    // Advanced Analytics Card Components
    const AdvancedAnalytics = () => {
      const [analyticsTab, setAnalyticsTab] = useState('stacked');

      // Calculate analytics data from actual runs
      const calculateModelAnalytics = () => {
        const modelStats = {};

        // Process each run to extract model-specific data
        allRuns.forEach(run => {
          if (run.llm_calls && run.llm_calls.length > 0) {
            // Process ALL LLM calls in each run, not just the first one
            run.llm_calls.forEach(call => {
              const model = call.model || 'Unknown';

              if (!modelStats[model]) {
                modelStats[model] = {
                  totalCost: 0,
                  inputCost: 0,
                  outputCost: 0,
                  totalTokens: 0,
                  inputTokens: 0,
                  outputTokens: 0,
                  runCount: 0
                };
              }

              const stats = modelStats[model];
              stats.totalCost += call.cost || 0;
              stats.inputCost += call.input_cost || (call.cost * 0.6) || 0; // Estimate if not available
              stats.outputCost += call.output_cost || (call.cost * 0.4) || 0; // Estimate if not available
              stats.totalTokens += call.total_tokens || 0;
              stats.inputTokens += call.input_tokens || 0;
              stats.outputTokens += call.output_tokens || 0;
              stats.runCount += 1;
            });
          }
        });

        return modelStats;
      };

      const modelAnalytics = calculateModelAnalytics();
      const models = Object.keys(modelAnalytics);

      // Debug: Log the models and their analytics
      console.log('Advanced Analytics - Models found:', models);
      console.log('Advanced Analytics - Model analytics:', modelAnalytics);

      // Calculate total usage for percentage calculations
      const totalUsage = Object.values(modelAnalytics).reduce((sum, stats) => sum + stats.runCount, 0);

      // Prepare data for stacked bar chart
      const stackedBarData = models.map(model => {
        const stats = modelAnalytics[model];
        return {
          model,
          inputCost: stats.inputCost,
          outputCost: stats.outputCost,
          totalCost: stats.totalCost
        };
      });

      // Prepare data for bubble chart
      const bubbleData = models.map(model => {
        const stats = modelAnalytics[model];
        return {
          model,
          totalTokens: stats.totalTokens,
          avgCostPerToken: stats.totalTokens > 0 ? stats.totalCost / stats.totalTokens : 0,
          totalCost: stats.totalCost,
          usagePercentage: totalUsage > 0 ? (stats.runCount / totalUsage) * 100 : 0
        };
      });

      // For heatmap visualization
      const heatmapData = [];
      const metrics = ['Input Cost', 'Output Cost', 'Avg Cost/Token', 'Total Cost'];

      models.forEach(model => {
        const stats = modelAnalytics[model];

        metrics.forEach(metric => {
          let value = 0;
          switch (metric) {
            case 'Input Cost':
              value = stats.inputCost;
              break;
            case 'Output Cost':
              value = stats.outputCost;
              break;
            case 'Avg Cost/Token':
              value = stats.totalTokens > 0 ? stats.totalCost / stats.totalTokens : 0;
              break;
            case 'Total Cost':
              value = stats.totalCost;
              break;
            default:
              value = 0;
              break;
          }

          heatmapData.push({
            model,
            metric,
            value
          });
        });
      });

      // Normalize data for heatmap coloring
      const normalizeValue = (value, metric) => {
        const metricValues = heatmapData
          .filter(item => item.metric === metric)
          .map(item => item.value);

        const min = Math.min(...metricValues);
        const max = Math.max(...metricValues);

        return max === min ? 0.5 : (value - min) / (max - min);
      };

      // Custom tooltip for stacked bar chart
      const StackedBarTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
          const data = payload[0].payload;
          return (
            <div className="bg-white p-3 shadow-md rounded-md border border-gray-200">
              <p className="font-medium text-sm font-inter">{data.model}</p>
              <div className="text-xs text-gray-700 mt-1 font-inter">
                <div className="flex items-center">
                  <div className="w-3 h-3 mr-1.5" style={{ backgroundColor: '#4F46E5' }}></div>
                  <span>Input Cost: ${data.inputCost.toFixed(8)}</span>
                </div>
                <div className="flex items-center mt-0.5">
                  <div className="w-3 h-3 mr-1.5" style={{ backgroundColor: '#10B981' }}></div>
                  <span>Output Cost: ${data.outputCost.toFixed(8)}</span>
                </div>
                <div className="mt-1 pt-1 border-t border-gray-200">
                  <span>Total Cost: ${data.totalCost.toFixed(5)}</span>
                </div>
              </div>
            </div>
          );
        }
        return null;
      };

      // Custom tooltip for bubble chart
      const BubbleChartTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
          const data = payload[0].payload;
          return (
            <div className="bg-white p-3 shadow-md rounded-md border border-gray-200">
              <p className="font-medium text-sm font-inter">{data.model}</p>
              <div className="text-xs text-gray-700 mt-1 font-inter">
                <div>Avg Cost/Token: ${data.avgCostPerToken.toFixed(8)}</div>
                <div>Total Tokens: {data.totalTokens}</div>
                <div>Total Cost: ${data.totalCost.toFixed(5)}</div>
                <div>Usage: {data.usagePercentage}%</div>
              </div>
            </div>
          );
        }
        return null;
      };

      // Custom tooltip for heatmap
      const HeatmapTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
          const data = payload[0].payload;
          return (
            <div className="bg-white p-3 shadow-md rounded-md border border-gray-200">
              <p className="font-medium text-sm font-inter">{data.model}</p>
              <div className="text-xs text-gray-700 mt-1 font-inter">
                <div>{data.metric}: ${data.value.toFixed(8)}</div>
                <div>Intensity: {(normalizeValue(data.value, data.metric) * 100).toFixed(0)}%</div>
              </div>
            </div>
          );
        }
        return null;
      };

      // Stack Bar Chart Component
      const StackedBarChart = () => (
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={stackedBarData}
              margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#EEF2F6" />
              <XAxis
                dataKey="model"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 10, fill: '#94A3B8', fontFamily: 'Inter, sans-serif' }}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 10, fill: '#94A3B8', fontFamily: 'Inter, sans-serif' }}
                tickFormatter={(value) => `$${value.toFixed(5)}`}
              />
              <Tooltip content={<StackedBarTooltip />} />
              <Legend
                verticalAlign="top"
                height={36}
                formatter={(value) => <span className="text-xs text-gray-600 font-inter">{value}</span>}
              />
              <Bar dataKey="inputCost" stackId="a" name="Input Cost" fill="#4F46E5" radius={[4, 4, 0, 0]} />
              <Bar dataKey="outputCost" stackId="a" name="Output Cost" fill="#10B981" radius={[0, 0, 4, 4]} />
            </BarChart>
          </ResponsiveContainer>
          <div className="text-center text-xs text-gray-500 mt-2">
            Stacked Bar: Input vs Output Cost per Model
          </div>
        </div>
      );

      // Bubble Chart Component
      const BubbleChart = () => (
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <ScatterChart
              margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F6" />
              <XAxis
                type="number"
                dataKey="totalTokens"
                name="Total Tokens"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 10, fill: '#94A3B8', fontFamily: 'Inter, sans-serif' }}
                domain={['auto', 'auto']}
              />
              <YAxis
                type="number"
                dataKey="avgCostPerToken"
                name="Avg Cost/Token"
                axisLine={false}
                tickLine={false}
                tick={{ fontSize: 10, fill: '#94A3B8', fontFamily: 'Inter, sans-serif' }}
                tickFormatter={(value) => `$${value.toFixed(6)}`}
              />
              <ZAxis
                type="number"
                dataKey="usagePercentage"
                range={[40, 100]}
                name="Usage %"
              />
              <Tooltip content={<BubbleChartTooltip />} />
              <Legend
                verticalAlign="top"
                height={36}
                formatter={(value) => <span className="text-xs text-gray-600 font-inter">{value}</span>}
              />
              <Scatter
                name="Model Performance"
                data={bubbleData}
                fill="#8884d8"
              >
                {bubbleData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={`hsl(${(index * 60) % 360}, 70%, 50%)`} />
                ))}
              </Scatter>
            </ScatterChart>
          </ResponsiveContainer>
          <div className="text-center text-xs text-gray-500 mt-2">
            Bubble Chart: Avg Cost/Token vs Usage vs Total Cost
          </div>
        </div>
      );

      // Heatmap Component - Adaptive based on single vs multiple models
      const HeatmapChart = () => {
        const isSingleModel = models.length === 1;

        if (isSingleModel) {
          // Single Model: Show horizontal bar chart for each metric
          const singleModelData = metrics.map(metric => {
            const model = models[0];
            const stats = modelAnalytics[model];
            let value = 0;

            switch (metric) {
              case 'Input Cost':
                value = stats.inputCost;
                break;
              case 'Output Cost':
                value = stats.outputCost;
                break;
              case 'Avg Cost/Token':
                value = stats.totalTokens > 0 ? stats.totalCost / stats.totalTokens : 0;
                break;
              case 'Total Cost':
                value = stats.totalCost;
                break;
              default:
                value = 0;
                break;
            }

            return {
              metric,
              value,
              model
            };
          });

          // Normalize values for color intensity
          const maxValue = Math.max(...singleModelData.map(d => d.value));
          const minValue = Math.min(...singleModelData.map(d => d.value));

          const CustomSingleModelTooltip = ({ active, payload }) => {
            if (active && payload && payload.length) {
              const data = payload[0].payload;
              const intensity = maxValue === minValue ? 0.5 : (data.value - minValue) / (maxValue - minValue);
              return (
                <div className="bg-white p-3 shadow-md rounded-md border border-gray-200">
                  <p className="font-medium text-sm font-inter">{data.model}</p>
                  <div className="text-xs text-gray-700 mt-1 font-inter">
                    <div>{data.metric}: ${data.value.toFixed(8)}</div>
                    <div>Intensity: {(intensity * 100).toFixed(0)}%</div>
                  </div>
                </div>
              );
            }
            return null;
          };

          return (
            <div className="h-64">
              <div className="mb-3 flex items-center justify-center">
                <span className={`${modelBadgeColor(models[0])} text-sm px-3 py-1 rounded-full font-medium`}>
                  {models[0]}
                </span>
              </div>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={singleModelData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                >
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#EEF2F6" />
                  <XAxis
                    type="category"
                    dataKey="metric"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 10, fill: '#94A3B8', fontFamily: 'Inter, sans-serif' }}
                    angle={-45}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis
                    type="number"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 10, fill: '#94A3B8', fontFamily: 'Inter, sans-serif' }}
                    tickFormatter={(value) => `$${value.toFixed(6)}`}
                  />
                  <Tooltip content={<CustomSingleModelTooltip />} />
                  <Bar
                    dataKey="value"
                    radius={[4, 4, 0, 0]}
                  >
                    {singleModelData.map((entry, index) => {
                      const intensity = maxValue === minValue ? 0.5 : (entry.value - minValue) / (maxValue - minValue);
                      const color = `rgba(79, 70, 229, ${Math.max(0.2, intensity)})`;
                      return <Cell key={`cell-${index}`} fill={color} />;
                    })}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <div className="text-center text-xs text-gray-500 mt-2">
                Single Model Heatmap: Cost metrics for {models[0]}
              </div>
            </div>
          );
        } else {
          // Multiple Models: Show traditional heatmap
          const CELL_HEIGHT = 40;
          const CELL_WIDTH = 100;

          return (
            <div className="h-64">
              <div className="mb-3 flex items-center justify-center gap-2 flex-wrap">
                {models.map((model, idx) => (
                  <span key={idx} className={`${modelBadgeColor(model)} text-xs px-2 py-1 rounded`}>
                    {model}
                  </span>
                ))}
              </div>
              <ResponsiveContainer width="100%" height="100%">
                <ScatterChart
                  margin={{ top: 20, right: 20, bottom: 20, left: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F6" />
                  <XAxis
                    type="category"
                    dataKey="model"
                    name="Model"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 10, fill: '#94A3B8', fontFamily: 'Inter, sans-serif' }}
                    interval={0}
                    tickMargin={5}
                  />
                  <YAxis
                    type="category"
                    dataKey="metric"
                    name="Metric"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 10, fill: '#94A3B8', fontFamily: 'Inter, sans-serif' }}
                    interval={0}
                  />
                  <Tooltip content={<HeatmapTooltip />} />
                  <Scatter
                    data={heatmapData}
                    shape={(props) => {
                      const { cx, cy, payload } = props;
                      const intensity = normalizeValue(payload.value, payload.metric);
                      const color = `rgba(79, 70, 229, ${Math.max(0.2, intensity)})`;

                      return (
                        <Rectangle
                          x={cx - CELL_WIDTH / 2}
                          y={cy - CELL_HEIGHT / 2}
                          width={CELL_WIDTH}
                          height={CELL_HEIGHT}
                          fill={color}
                          stroke="#fff"
                          className="heatmap-cell"
                        />
                      );
                    }}
                  />
                </ScatterChart>
              </ResponsiveContainer>
              <div className="text-center text-xs text-gray-500 mt-2">
                Multi-Model Heatmap: Cost intensity comparison across models
              </div>
            </div>
          );
        }
      };

      return (
        <>
          <div className="flex justify-between items-center mb-4">
            <div>
              <h3 className="text-xl font-bold text-black">Advanced Analytics</h3>
              <div className="flex items-center gap-2 mt-2">
                <span className="text-sm text-gray-500">Analyzing {models.length} model{models.length !== 1 ? 's' : ''}:</span>
                <div className="flex gap-1">
                  {models.map((model, idx) => (
                    <span key={idx} className={`${modelBadgeColor(model)} text-xs px-2 py-1 rounded`}>
                      {model}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex bg-gray-100 rounded-md p-1">
              <button
                className={`flex items-center px-3 py-1 text-xs rounded-md ${analyticsTab === 'stacked' ? 'bg-white shadow' : 'text-gray-500'}`}
                onClick={() => setAnalyticsTab('stacked')}
              >
                <FaChartBar className="mr-1" /> Stacked
              </button>
              <button
                className={`flex items-center px-3 py-1 text-xs rounded-md ${analyticsTab === 'bubble' ? 'bg-white shadow' : 'text-gray-500'}`}
                onClick={() => setAnalyticsTab('bubble')}
              >
                <FaChartPie className="mr-1" /> Bubble
              </button>
              <button
                className={`flex items-center px-3 py-1 text-xs rounded-md ${analyticsTab === 'heatmap' ? 'bg-white shadow' : 'text-gray-500'}`}
                onClick={() => setAnalyticsTab('heatmap')}
              >
                <FaThermometerThreeQuarters className="mr-1" /> Heatmap
              </button>
            </div>
          </div>
          <div className="bg-gray-50 rounded-lg p-4">
            {analyticsTab === 'stacked' && <StackedBarChart />}
            {analyticsTab === 'bubble' && <BubbleChart />}
            {analyticsTab === 'heatmap' && <HeatmapChart />}
          </div>
        </>
      );
    };

    // Run Details Modal Component
    const RunDetailsModal = ({ run }) => {
      // Tab state for modal
      const [activeModalTab, setActiveModalTab] = useState('Overview');

      // Use Maps to track dropdown state for each LLM call independently
      const [inputOutputOpenMap, setInputOutputOpenMap] = useState(new Map());
      const [governanceOpenMap, setGovernanceOpenMap] = useState(new Map());
      const [ocrTextOpenMap, setOcrTextOpenMap] = useState(new Map());
      const [webSearchMetricsOpenMap, setWebSearchMetricsOpenMap] = useState(new Map());

      // Traces state
      const [tracesData, setTracesData] = useState({}); // { traceId: { trace, spans, parentSpans } }
      const [expandedTraces, setExpandedTraces] = useState(new Set()); // Set of expanded trace IDs
      const [expandedParentSpans, setExpandedParentSpans] = useState(new Set()); // Set of expanded parent span IDs
      const [loadingTraces, setLoadingTraces] = useState(new Set()); // Set of trace IDs being loaded
      const [selectedTrace, setSelectedTrace] = useState(null); // Selected trace for full trace modal
      const [showTraceModal, setShowTraceModal] = useState(false); // Show/hide trace modal
      const [selectedSpanInTrace, setSelectedSpanInTrace] = useState(null); // Currently selected span in trace modal
      const [activeSpanTab, setActiveSpanTab] = useState('Call'); // Active tab in span details
      const [tracesLoaded, setTracesLoaded] = useState(false); // Flag to track if traces have been loaded for current run

      // Clear loading traces when modal opens (in case of stale state)
      useEffect(() => {
        if (run) {
          setLoadingTraces(new Set());
          setTracesData({});
          setTracesLoaded(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [run?.run_id]);

      // Helper functions to toggle dropdowns for specific calls
      const toggleInputOutput = (callIndex) => {
        setInputOutputOpenMap(prev => {
          const newMap = new Map(prev);
          newMap.set(callIndex, !newMap.get(callIndex));
          return newMap;
        });
      };

      const toggleGovernance = (callIndex) => {
        setGovernanceOpenMap(prev => {
          const newMap = new Map(prev);
          newMap.set(callIndex, !newMap.get(callIndex));
          return newMap;
        });
      };

      const toggleOcrText = (index) => {
        setOcrTextOpenMap(prev => {
          const newMap = new Map(prev);
          newMap.set(index, !newMap.get(index));
          return newMap;
        });
      };

      const toggleWebSearchMetrics = (index) => {
        setWebSearchMetricsOpenMap(prev => {
          const newMap = new Map(prev);
          newMap.set(index, !newMap.get(index));
          return newMap;
        });
      };

      // Function to load trace data
      const loadTraceData = useCallback(async (traceId) => {
        setLoadingTraces(prev => new Set(prev).add(traceId));

        try {
          // Get project name dynamically
          const projectName = project?.name || project?.project || project?.project_name;

          if (!projectName) {
            console.error('No project name available');
            return;
          }

          const response = await fetchTraces([traceId], projectName);
          const traceData = response.data?.traces?.[0];

          if (traceData) {
            // Organize spans by parent-child relationships
            const spans = traceData.spans || [];
            const parentSpansMap = {};

            // Get root span
            const rootSpanId = traceData.root_span?.span_id;

            // Use parent_spans mapping from trace data if available
            const parentSpansFromTrace = traceData.parent_spans || {};

            // Build a map of parent span ID to child span IDs
            const parentToChildrenMap = {};
            Object.keys(parentSpansFromTrace).forEach(parentId => {
              const childIds = parentSpansFromTrace[parentId] || [];
              parentToChildrenMap[parentId] = childIds.map(childId => {
                return spans.find(s => s.span_id === childId);
              }).filter(Boolean);
            });

            // Also organize spans by parent_span_id for direct parent-child relationships
            spans.forEach(span => {
              const parentId = span.parent_span_id || rootSpanId;
              if (!parentSpansMap[parentId]) {
                parentSpansMap[parentId] = [];
              }
              parentSpansMap[parentId].push(span);
            });

            setTracesData(prev => ({
              ...prev,
              [traceId]: {
                trace: traceData,
                spans: spans,
                parentSpansMap: parentSpansMap,
                parentToChildrenMap: parentToChildrenMap,
                rootSpanId: rootSpanId
              }
            }));
          }
        } catch (error) {
          console.error(`Error loading trace ${traceId}:`, error);
        } finally {
          setLoadingTraces(prev => {
            const newSet = new Set(prev);
            newSet.delete(traceId);
            return newSet;
          });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, []);

      // Auto-load trace data when Traces tab is opened
      useEffect(() => {
        if (!run || activeModalTab !== 'Traces' || tracesLoaded) {
          return;
        }

        // Mark as loaded to prevent re-triggering
        setTracesLoaded(true);

        // Extract trace IDs from agent_metrics
        let traces = null;

        // Check multiple possible locations for traces
        if (run.agent_metrics && typeof run.agent_metrics === 'object' && !Array.isArray(run.agent_metrics) && run.agent_metrics.traces) {
          traces = run.agent_metrics.traces;
        } else if (Array.isArray(run.agent_metrics) && run.agent_metrics.length > 0) {
          traces = run.agent_metrics;
        } else if (run.traces) {
          traces = run.traces;
        } else if (run.trace_id) {
          traces = [run.trace_id];
        }

        if (traces && Array.isArray(traces)) {
          const extractedIds = traces.map(t => {
            if (typeof t === 'object' && t !== null && 'S' in t) return t.S;
            if (typeof t === 'string') return t;
            if (typeof t === 'object' && t !== null && t.trace_id) return t.trace_id;
            return null;
          }).filter(Boolean);

          // Load all traces
          extractedIds.forEach(traceId => {
            loadTraceData(traceId);
          });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [run?.run_id, activeModalTab, tracesLoaded]);

      if (!run) return null;

      const callData = run.llm_calls[0] || {};
      const customLogs = run.custom_logs || {};

      // Use real logs from the run data
      const logs = run.logs || [];

      // Extract trace IDs from run
      const getTraceIdsFromRun = () => {
        let traces = null;

        if (run.agent_metrics && typeof run.agent_metrics === 'object' && !Array.isArray(run.agent_metrics) && run.agent_metrics.traces) {
          traces = run.agent_metrics.traces;
        } else if (Array.isArray(run.agent_metrics) && run.agent_metrics.length > 0) {
          traces = run.agent_metrics;
        } else if (run.traces) {
          traces = run.traces;
        } else if (run.trace_id) {
          traces = [run.trace_id];
        }

        if (traces && Array.isArray(traces)) {
          const extractedIds = traces.map(t => {
            if (typeof t === 'object' && t !== null && 'S' in t) return t.S;
            if (typeof t === 'string') return t;
            if (typeof t === 'object' && t !== null && t.trace_id) return t.trace_id;
            return null;
          }).filter(Boolean);
          return extractedIds;
        }

        return [];
      };

      const traceIds = getTraceIdsFromRun();

      // Toggle trace expansion
      const toggleTrace = (traceId) => {
        setExpandedTraces(prev => {
          const newSet = new Set(prev);
          if (newSet.has(traceId)) {
            newSet.delete(traceId);
          } else {
            newSet.add(traceId);
            loadTraceData(traceId);
          }
          return newSet;
        });
      };

      // Toggle parent span expansion
      const toggleParentSpan = (traceId, parentSpanId) => {
        const key = `${traceId}:${parentSpanId}`;
        setExpandedParentSpans(prev => {
          const newSet = new Set(prev);
          if (newSet.has(key)) {
            newSet.delete(key);
          } else {
            newSet.add(key);
          }
          return newSet;
        });
      };

      // Handler to open trace modal (LangSmith-style)
      const handleTraceClick = (traceId) => {
        const traceData = tracesData[traceId];
        if (traceData) {
          setSelectedTrace({ traceId, ...traceData });
          setShowTraceModal(true);
          if (traceData.trace?.root_span) {
            setSelectedSpanInTrace(traceData.trace.root_span);
          }
        }
      };

      // Handler to close trace modal
      const closeTraceModal = () => {
        setShowTraceModal(false);
        setSelectedTrace(null);
        setSelectedSpanInTrace(null);
        setActiveSpanTab('Call');
      };

      // Handler to select a span within the trace modal
      const handleSpanSelectInTrace = (span) => {
        setSelectedSpanInTrace(span);
      };

      // Trace Details Modal Component (LangSmith-style)
      const TraceDetailsModal = ({ trace, onClose }) => {
        // Initialize expanded nodes - must be before early return
        const [expandedSpanNodes, setExpandedSpanNodes] = useState(new Set());

        // Initialize with all parent nodes expanded (only once when trace changes)
        useEffect(() => {
          if (!trace?.traceId) return;

          const spans = trace.spans || [];
          const rootSpan = trace.trace?.root_span;
          const parentSpans = trace.trace?.parent_spans || {};

          // Build tree to collect parent span IDs
          const collectAllParentSpanIds = () => {
            const parentIds = new Set();

            if (!rootSpan) return parentIds;

            const rootSpanId = rootSpan.span_id;

            const buildNode = (spanId, depth = 0) => {
              const span = spans.find(s => s.span_id === spanId) || (spanId === rootSpanId ? rootSpan : null);
              if (!span) return null;

              const children = parentSpans[spanId] || [];

              if (children.length > 0) {
                parentIds.add(spanId);
                children.forEach(childId => buildNode(childId, depth + 1));
              }

              return true;
            };

            buildNode(rootSpanId, 0);
            return parentIds;
          };

          setExpandedSpanNodes(collectAllParentSpanIds());
          // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [trace?.traceId]); // Only run when trace ID changes

        if (!trace) return null;

        const spans = trace.spans || [];
        const rootSpan = trace.trace?.root_span;
        const parentSpans = trace.trace?.parent_spans || {};

        // Build tree structure for spans
        const buildSpanTree = () => {
          if (!rootSpan) return [];

          const rootSpanId = rootSpan.span_id;

          const buildNode = (spanId, depth = 0) => {
            const span = spans.find(s => s.span_id === spanId) || (spanId === rootSpanId ? rootSpan : null);
            if (!span) return null;

            const children = parentSpans[spanId] || [];

            return {
              span,
              spanId,
              depth,
              children: children.map(childId => buildNode(childId, depth + 1)).filter(Boolean),
              hasChildren: children.length > 0
            };
          };

          return [buildNode(rootSpanId, 0)].filter(Boolean);
        };

        const spanTree = buildSpanTree();
        const currentSpan = selectedSpanInTrace || rootSpan;

        // Toggle expansion of span node
        const toggleSpanExpansion = (spanId, e) => {
          e.stopPropagation();
          setExpandedSpanNodes(prev => {
            const newSet = new Set(prev);
            if (newSet.has(spanId)) {
              newSet.delete(spanId);
            } else {
              newSet.add(spanId);
            }
            return newSet;
          });
        };

        // Render span tree node recursively
        const renderSpanNode = (node) => {
          if (!node) return null;

          const isSelected = currentSpan?.span_id === node.spanId;
          const hasChildren = node.children.length > 0;
          const isExpanded = expandedSpanNodes.has(node.spanId);

          return (
            <div key={node.spanId}>
              <div
                onClick={() => handleSpanSelectInTrace(node.span)}
                className={`
              flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-gray-100 rounded
              ${isSelected ? 'bg-blue-50 border-l-4 border-blue-500' : ''}
            `}
                style={{ paddingLeft: `${node.depth * 1.5 + 0.75}rem` }}
              >
                {hasChildren && (
                  <button
                    onClick={(e) => toggleSpanExpansion(node.spanId, e)}
                    className="hover:bg-gray-200 rounded p-0.5"
                  >
                    <FiChevronRight
                      className={`text-xs text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                    />
                  </button>
                )}
                {!hasChildren && <div className="w-4"></div>}
                <span className="text-xs font-mono text-gray-700 truncate">
                  {node.span?.name || node.spanId}
                </span>
                <span className="text-xs text-gray-400 ml-auto">
                  {node.span?.duration_seconds ? `${safeToFixed(node.span.duration_seconds, 2)}s` : ''}
                </span>
              </div>
              {hasChildren && isExpanded && node.children.map(child => renderSpanNode(child))}
            </div>
          );
        };

        return (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-[10000] flex justify-center items-center p-4"
            onClick={onClose}
          >
            <div
              className="bg-white rounded-lg shadow-2xl w-full h-full max-w-7xl max-h-[95vh] flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex justify-between items-center border-b px-6 py-4 bg-gray-50">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">
                    {rootSpan?.name || 'Trace Details'}
                  </h2>
                  <p className="text-sm text-gray-500 font-mono mt-1">{trace.traceId}</p>
                </div>
                <button
                  onClick={onClose}
                  className="text-gray-500 hover:text-gray-700 transition-colors p-2"
                >
                  <FaTimes size={20} />
                </button>
              </div>

              {/* Main Content */}
              <div className="flex flex-1 overflow-hidden">
                {/* Left Sidebar - Span Tree */}
                <div className="w-80 border-r bg-gray-50 overflow-y-auto">
                  <div className="p-4">
                    <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase">Span Tree</h3>
                    <div className="space-y-1">
                      {spanTree.map(node => renderSpanNode(node))}
                    </div>
                  </div>
                </div>

                {/* Right Panel - Span Details */}
                <div className="flex-1 flex flex-col overflow-hidden">
                  {/* Tabs */}
                  <div className="border-b bg-white">
                    <div className="flex gap-6 px-6">
                      {['Call', 'Summary'].map(tab => (
                        <button
                          key={tab}
                          onClick={() => setActiveSpanTab(tab)}
                          className={`
                        py-3 text-sm font-medium border-b-2 transition-colors
                        ${activeSpanTab === tab
                              ? 'border-blue-500 text-blue-600'
                              : 'border-transparent text-gray-500 hover:text-gray-700'}
                      `}
                        >
                          {tab}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Tab Content */}
                  <div className="flex-1 overflow-y-auto p-6 bg-white">
                    {activeSpanTab === 'Call' && currentSpan && (
                      <div>
                        {/* Span Info Header */}
                        <div className="mb-6 pb-4 border-b">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                              {currentSpan.observation_type || 'span'}
                            </span>
                            <h3 className="text-xl font-semibold text-gray-900">
                              {currentSpan.name || currentSpan.span_id}
                            </h3>
                            {(currentSpan.model_name || currentSpan.model) && (
                              <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                                {currentSpan.model_name || currentSpan.model}
                              </span>
                            )}
                          </div>
                          <div className="flex gap-6 text-sm text-gray-600">
                            <span>Duration: <span className="font-semibold text-gray-900">
                              {currentSpan.duration_seconds !== undefined && currentSpan.duration_seconds !== null
                                ? `${safeToFixed(currentSpan.duration_seconds, 3)}s`
                                : (trace.trace?.total_duration_seconds ? `${safeToFixed(trace.trace.total_duration_seconds, 3)}s` : '-')}
                            </span></span>
                            {(currentSpan.created_at || trace.trace?.created_at) && (
                              <span>Created: <span className="font-semibold text-gray-900">
                                {new Date(currentSpan.created_at || trace.trace?.created_at).toLocaleString()}
                              </span></span>
                            )}
                          </div>
                        </div>

                        {/* Inputs Section */}
                        {currentSpan.input && (
                          <div className="mb-6">
                            <div className="flex items-center gap-2 mb-3">
                              <h4 className="text-sm font-semibold text-gray-700 uppercase">Inputs</h4>
                            </div>
                            <div className="bg-gray-50 border border-gray-200 rounded-lg overflow-hidden">
                              {(() => {
                                let inputData = currentSpan.input;
                                if (typeof inputData === 'string') {
                                  try {
                                    inputData = JSON.parse(inputData);
                                  } catch (e) {
                                    // Keep as string if not valid JSON
                                  }
                                }

                                if (typeof inputData === 'object' && inputData !== null) {
                                  return (
                                    <div className="divide-y divide-gray-200">
                                      {Object.entries(inputData).map(([key, value]) => (
                                        <div key={key} className="px-4 py-3">
                                          <div className="flex justify-between items-start mb-1">
                                            <span className="text-xs font-semibold text-gray-600 uppercase">
                                              {key}
                                            </span>
                                            <span className="text-xs text-gray-400">
                                              {typeof value}
                                            </span>
                                          </div>
                                          <div className="bg-white border border-gray-200 rounded p-3 mt-2">
                                            {value === '' || value === null || value === undefined ? (
                                              <span className="text-sm text-gray-400 italic">Empty</span>
                                            ) : (
                                              <pre className="text-sm text-gray-900 whitespace-pre-wrap break-words font-mono">
                                                {typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}
                                              </pre>
                                            )}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  );
                                }

                                return (
                                  <div className="p-4">
                                    <pre className="text-sm text-gray-900 whitespace-pre-wrap break-words font-mono">
                                      {String(inputData)}
                                    </pre>
                                  </div>
                                );
                              })()}
                            </div>
                          </div>
                        )}

                        {/* Output Section */}
                        {currentSpan.output && (
                          <div className="mb-6">
                            <div className="flex items-center gap-2 mb-3">
                              <h4 className="text-sm font-semibold text-gray-700 uppercase">Output</h4>
                            </div>
                            <div className="bg-gray-50 border border-gray-200 rounded-lg overflow-hidden">
                              {(() => {
                                let outputData = currentSpan.output;
                                if (typeof outputData === 'string') {
                                  try {
                                    outputData = JSON.parse(outputData);
                                  } catch (e) {
                                    // Keep as string if not valid JSON
                                  }
                                }

                                if (typeof outputData === 'object' && outputData !== null) {
                                  return (
                                    <div className="divide-y divide-gray-200">
                                      {Object.entries(outputData).map(([key, value]) => (
                                        <div key={key} className="px-4 py-3">
                                          <div className="flex justify-between items-start mb-1">
                                            <span className="text-xs font-semibold text-gray-600 uppercase">
                                              {key}
                                            </span>
                                            <span className="text-xs text-gray-400">
                                              {typeof value}
                                            </span>
                                          </div>
                                          <div className="bg-white border border-gray-200 rounded p-3 mt-2">
                                            {value === '' || value === null || value === undefined ? (
                                              <span className="text-sm text-gray-400 italic">Empty</span>
                                            ) : (
                                              <pre className="text-sm text-gray-900 whitespace-pre-wrap break-words font-mono">
                                                {typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}
                                              </pre>
                                            )}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  );
                                }

                                return (
                                  <div className="p-4">
                                    <pre className="text-sm text-gray-900 whitespace-pre-wrap break-words font-mono">
                                      {String(outputData)}
                                    </pre>
                                  </div>
                                );
                              })()}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {activeSpanTab === 'Summary' && currentSpan && (
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Span Summary</h3>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                            <p className="text-xs text-gray-500 uppercase mb-1">Span ID</p>
                            <p className="font-mono text-sm text-gray-900 break-all">{currentSpan.span_id}</p>
                          </div>
                          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                            <p className="text-xs text-gray-500 uppercase mb-1">Type</p>
                            <p className="text-sm text-gray-900">{currentSpan.observation_type || '-'}</p>
                          </div>
                          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                            <p className="text-xs text-gray-500 uppercase mb-1">Duration</p>
                            <p className="text-sm text-gray-900">
                              {currentSpan.duration_seconds !== undefined && currentSpan.duration_seconds !== null
                                ? `${safeToFixed(currentSpan.duration_seconds, 3)}s`
                                : (trace.trace?.total_duration_seconds ? `${safeToFixed(trace.trace.total_duration_seconds, 3)}s` : '-')}
                            </p>
                          </div>
                          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                            <p className="text-xs text-gray-500 uppercase mb-1">Created At</p>
                            <p className="text-sm text-gray-900">
                              {(currentSpan.created_at || trace.trace?.created_at)
                                ? new Date(currentSpan.created_at || trace.trace?.created_at).toLocaleString()
                                : '-'}
                            </p>
                          </div>
                          {(currentSpan.model_name || currentSpan.model) && (
                            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                              <p className="text-xs text-gray-500 uppercase mb-1">Model</p>
                              <p className="text-sm text-gray-900">{currentSpan.model_name || currentSpan.model}</p>
                            </div>
                          )}
                        </div>

                        {/* All Fields */}
                        <div className="mt-6">
                          <h4 className="text-sm font-semibold text-gray-700 mb-3">All Fields</h4>
                          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                            <pre className="text-xs text-gray-900 whitespace-pre-wrap break-words font-mono">
                              {JSON.stringify(currentSpan, null, 2)}
                            </pre>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      };

      return (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex justify-center items-center p-4"
          onClick={closeModal}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center border-b p-4">
              <h2 className="text-xl font-semibold">Run Details</h2>
              <button onClick={closeModal} className="text-gray-500 hover:text-gray-700">
                <FaTimes />
              </button>
            </div>

            {/* Tab Navigation */}
            <div className="border-b border-gray-200">
              <nav className="flex space-x-1 px-4" aria-label="Tabs">
                {['Overview', 'LLM Calls', 'Metrics', 'Traces', 'Logs', 'System'].map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveModalTab(tab)}
                    className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${activeModalTab === tab
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                  >
                    {tab}
                  </button>
                ))}
              </nav>
            </div>

            {/* Tab Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {/* Overview Tab */}
              {activeModalTab === 'Overview' && (
                <div className="mb-8">
                  <h3 className="text-lg font-medium mb-4">Performance Metrics</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Project:</p>
                      <p className="font-medium">{project?.name || project?.project}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Run ID:</p>
                      <p className="font-medium">{run.run_id}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Start Time:</p>
                      <p className="font-medium">
                        {new Date(run.start_time).toLocaleString('en-US', {
                          month: 'numeric',
                          day: 'numeric',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                          second: '2-digit'
                        })}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">End Time:</p>
                      <p className="font-medium">
                        {new Date(run.end_time).toLocaleString('en-US', {
                          month: 'numeric',
                          day: 'numeric',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                          second: '2-digit'
                        })}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Duration:</p>
                      <p className="font-medium">{safeToFixed(run.execution_duration, 2)}s</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Total Tokens:</p>
                      <p className="font-medium">{callData.total_tokens || 0}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Total Cost:</p>
                      <p className="font-medium">${safeToFixed(callData.cost, 5, "0.00000")}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Average Latency:</p>
                      <p className="font-medium">{safeToFixed(callData.latency_ms, 2, "0")}ms</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Status:</p>
                      <p className="font-medium">
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${run.status === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                          {run.status}
                        </span>
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* LLM Calls Tab */}
              {activeModalTab === 'LLM Calls' && (
                <div className="mb-8">
                  <h3 className="text-lg font-medium mb-4">LLM Call Chain</h3>
                  {!run.llm_calls || run.llm_calls.length === 0 ? (
                    <div className="bg-gray-50 rounded-lg p-8 text-center">
                      <p className="text-gray-500 text-sm">No LLM calls available for this run</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {run.llm_calls.map((call, callIndex) => (
                        <div key={callIndex} className="bg-gray-50 rounded-lg p-4">
                          <div className="font-medium mb-2">{call.name || `Call ${callIndex + 1}`}</div>
                          <div className="grid grid-cols-2 gap-3 mb-4 text-sm">
                            <div>
                              <p className="text-gray-600">Model:</p>
                              <p>
                                <span className={`${modelBadgeColor(call.model)} text-xs px-2 py-1 rounded`}>
                                  {call.model}
                                </span>
                              </p>
                            </div>
                            <div>
                              <p className="text-gray-600">Input Tokens:</p>
                              <p>{call.input_tokens}</p>
                            </div>
                            <div>
                              <p className="text-gray-600">Output Tokens:</p>
                              <p>{call.output_tokens}</p>
                            </div>
                            <div>
                              <p className="text-gray-600">Total Tokens:</p>
                              <p>{call.total_tokens}</p>
                            </div>
                            <div>
                              <p className="text-gray-600">Latency:</p>
                              <p>{safeToFixed(call.latency_ms, 2, "0")}ms</p>
                            </div>
                            <div>
                              <p className="text-gray-600">Cost:</p>
                              <p>
                                ${safeToFixed(call.cost, 5, "0.00000")}
                                {call.token_cost !== undefined && call.search_cost_breakdown?.search_cost !== undefined && (
                                  <span className="text-xs text-gray-500 block">
                                    (Tokens: ${safeToFixed(call.token_cost, 5)} + Search: ${safeToFixed(call.search_cost_breakdown.search_cost, 5)})
                                  </span>
                                )}
                              </p>
                            </div>
                          </div>

                          {/* Input/Output */}
                          <div className="border-t border-gray-200 pt-3">
                            <div className="flex items-center justify-between text-blue-700 font-medium cursor-pointer" onClick={() => toggleInputOutput(callIndex)}>
                              <span>Prompt/Response</span>
                              <svg className={`w-4 h-4 transition-transform ${inputOutputOpenMap.get(callIndex) || false ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </div>

                            {(inputOutputOpenMap.get(callIndex) || false) && (
                              <div className="mt-3 space-y-3">
                                <div>
                                  <p className="text-sm font-medium mb-1">Prompt:</p>
                                  <div className="bg-gray-100 p-3 rounded text-sm max-h-96 overflow-y-auto border border-gray-200">
                                    <pre className="whitespace-pre-wrap break-words font-mono text-xs" style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                                      {typeof call.prompt === 'object' ? JSON.stringify(call.prompt, null, 2) : (call.prompt || 'No prompt available')}
                                    </pre>
                                  </div>
                                </div>
                                <div>
                                  <p className="text-sm font-medium mb-1">Response:</p>
                                  <div className="bg-gray-100 p-3 rounded text-sm max-h-96 overflow-y-auto border border-gray-200">
                                    <pre className="whitespace-pre-wrap break-words font-mono text-xs" style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                                      {typeof call.response === 'object' ? JSON.stringify(call.response, null, 2) : (call.response || 'No response available')}
                                    </pre>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Governance & Safety Metrics */}
                          <div className="border-t border-gray-200 pt-3 mt-3">
                            <div className="flex items-center justify-between text-green-700 font-medium cursor-pointer" onClick={() => toggleGovernance(callIndex)}>
                              <span>Governance & Safety Metrics</span>
                              <svg className={`w-4 h-4 transition-transform ${governanceOpenMap.get(callIndex) || false ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </div>

                            {(governanceOpenMap.get(callIndex) || false) && (
                              <div className="mt-3">
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                  {/* Content Safety */}
                                  <div className="bg-green-50 rounded-lg p-4">
                                    <h4 className="font-semibold text-sm text-gray-800 mb-3">Content Safety</h4>
                                    <div className="space-y-2">
                                      <div className="flex items-center justify-between">
                                        <span className="text-xs text-gray-600">Hate Speech:</span>
                                        <span className={`px-2 py-1 text-xs font-semibold rounded ${(call.governance?.hate_speech || call.hate_speech || 'LOW') === 'HIGH' ? 'bg-red-100 text-red-800' :
                                          (call.governance?.hate_speech || call.hate_speech || 'LOW') === 'MEDIUM' ? 'bg-yellow-100 text-yellow-800' :
                                            'bg-green-100 text-green-800'
                                          }`}>
                                          {call.governance?.hate_speech || call.hate_speech || 'LOW'}
                                        </span>
                                      </div>
                                      <div className="flex items-center justify-between">
                                        <span className="text-xs text-gray-600">Violence:</span>
                                        <span className={`px-2 py-1 text-xs font-semibold rounded ${(call.governance?.violence || call.violence || 'LOW') === 'HIGH' ? 'bg-red-100 text-red-800' :
                                          (call.governance?.violence || call.violence || 'LOW') === 'MEDIUM' ? 'bg-yellow-100 text-yellow-800' :
                                            'bg-green-100 text-green-800'
                                          }`}>
                                          {call.governance?.violence || call.violence || 'LOW'}
                                        </span>
                                      </div>
                                      <div className="flex items-center justify-between">
                                        <span className="text-xs text-gray-600">Safe Content:</span>
                                        <span className={`px-2 py-1 text-xs font-semibold rounded ${(call.governance?.safe_content || call.safe_content || 'HIGH') === 'HIGH' ? 'bg-green-100 text-green-800' :
                                          (call.governance?.safe_content || call.safe_content || 'HIGH') === 'MEDIUM' ? 'bg-yellow-100 text-yellow-800' :
                                            'bg-red-100 text-red-800'
                                          }`}>
                                          {call.governance?.safe_content || call.safe_content || 'HIGH'}
                                        </span>
                                      </div>
                                    </div>
                                  </div>

                                  {/* Prompt Injection */}
                                  <div className="bg-blue-50 rounded-lg p-4">
                                    <h4 className="font-semibold text-sm text-gray-800 mb-3">Prompt Injection</h4>
                                    <div className="space-y-2">
                                      <div className="flex items-center justify-between">
                                        <span className="text-xs text-gray-600">Injection Detected:</span>
                                        <span className={`px-2 py-1 text-xs font-semibold rounded ${(call.governance?.injection_detected || call.injection_detected || 'LOW') === 'HIGH' ? 'bg-red-100 text-red-800' :
                                          (call.governance?.injection_detected || call.injection_detected || 'LOW') === 'MEDIUM' ? 'bg-yellow-100 text-yellow-800' :
                                            'bg-green-100 text-green-800'
                                          }`}>
                                          {call.governance?.injection_detected || call.injection_detected || 'LOW'}
                                        </span>
                                      </div>
                                      <div className="flex items-center justify-between">
                                        <span className="text-xs text-gray-600">Clean Prompt:</span>
                                        <span className={`px-2 py-1 text-xs font-semibold rounded ${(call.governance?.clean_prompt || call.clean_prompt || 'HIGH') === 'HIGH' ? 'bg-green-100 text-green-800' :
                                          (call.governance?.clean_prompt || call.clean_prompt || 'HIGH') === 'MEDIUM' ? 'bg-yellow-100 text-yellow-800' :
                                            'bg-red-100 text-red-800'
                                          }`}>
                                          {call.governance?.clean_prompt || call.clean_prompt || 'HIGH'}
                                        </span>
                                      </div>
                                    </div>
                                  </div>

                                  {/* Response Quality */}
                                  <div className="bg-yellow-50 rounded-lg p-4">
                                    <h4 className="font-semibold text-sm text-gray-800 mb-3">Response Quality</h4>
                                    <div className="space-y-2">
                                      <div className="flex items-center justify-between">
                                        <span className="text-xs text-gray-600">Quality Rating:</span>
                                        <span className={`px-2 py-1 text-xs font-semibold rounded ${(call.governance?.quality_rating || call.quality_rating || 'MEDIUM') === 'HIGH' ? 'bg-green-100 text-green-800' :
                                          (call.governance?.quality_rating || call.quality_rating || 'MEDIUM') === 'MEDIUM' ? 'bg-yellow-100 text-yellow-800' :
                                            'bg-red-100 text-red-800'
                                          }`}>
                                          {call.governance?.quality_rating || call.quality_rating || 'MEDIUM'}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Web Search Metrics */}
                          {call.web_search_metrics && (
                            <div className="border-t border-gray-200 pt-3 mt-3">
                              <div className="flex items-center justify-between text-indigo-700 font-medium cursor-pointer" onClick={() => toggleWebSearchMetrics(callIndex)}>
                                <span>Web Search Metrics</span>
                                <svg className={`w-4 h-4 transition-transform ${webSearchMetricsOpenMap.get(callIndex) || false ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                              </div>

                              {(webSearchMetricsOpenMap.get(callIndex) || false) && (
                                <div className="mt-3 space-y-4">
                                  {/* Overview & Stats */}
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="bg-indigo-50 p-3 rounded-lg">
                                      <h4 className="text-sm font-medium text-indigo-800 mb-2">Search Overview</h4>
                                      <div className="space-y-1 text-xs">
                                        <div className="flex justify-between">
                                          <span>Provider:</span>
                                          <span className="font-medium text-indigo-900">{call.web_search_metrics.provider_type || 'N/A'}</span>
                                        </div>
                                        <div className="flex justify-between">
                                          <span>Enabled:</span>
                                          <span className={`px-2 py-0.5 rounded text-xs ${call.web_search_metrics.enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                                            {call.web_search_metrics.enabled ? 'YES' : 'NO'}
                                          </span>
                                        </div>
                                        <div className="flex justify-between">
                                          <span>Search Count:</span>
                                          <span className="font-medium text-indigo-900">{call.web_search_metrics.search_count || 0}</span>
                                        </div>
                                      </div>
                                    </div>

                                    <div className="bg-blue-50 p-3 rounded-lg">
                                      <h4 className="text-sm font-medium text-blue-800 mb-2">Search Stats</h4>
                                      <div className="space-y-1 text-xs">
                                        <div className="flex justify-between">
                                          <span>Sources Found:</span>
                                          <span className="font-medium text-blue-900">{call.web_search_metrics.sources_count || 0}</span>
                                        </div>
                                        <div className="flex justify-between">
                                          <span>Citations Used:</span>
                                          <span className="font-medium text-blue-900">{call.web_search_metrics.citations_count || 0}</span>
                                        </div>
                                        {call.web_search_metrics.search_cost_breakdown?.search_cost !== undefined && (
                                          <div className="flex justify-between">
                                            <span>Search Cost:</span>
                                            <span className="font-medium text-blue-900">${safeToFixed(call.web_search_metrics.search_cost_breakdown.search_cost, 6)}</span>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>

                                  {/* Citations List */}
                                  {call.web_search_metrics.citations && call.web_search_metrics.citations.length > 0 && (
                                    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                                      <div className="bg-gray-50 px-3 py-2 border-b border-gray-200">
                                        <h4 className="text-sm font-medium text-gray-700">Citations ({call.web_search_metrics.citations.length})</h4>
                                      </div>
                                      <div className="max-h-48 overflow-y-auto divide-y divide-gray-100">
                                        {call.web_search_metrics.citations.map((citation, idx) => (
                                          <div key={idx} className="px-3 py-2 hover:bg-gray-50 transition-colors">
                                            <a href={citation.url} target="_blank" rel="noopener noreferrer" className="flex items-start gap-2 group">
                                              <span className="text-xs font-mono text-gray-400 mt-0.5">[{idx + 1}]</span>
                                              <div className="flex-1 min-w-0">
                                                <p className="text-sm text-blue-600 group-hover:underline truncate font-medium">
                                                  {citation.title || citation.url}
                                                </p>
                                                <p className="text-xs text-gray-500 truncate">{citation.url}</p>
                                              </div>
                                            </a>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Metrics Tab */}
              {activeModalTab === 'Metrics' && (
                <div className="space-y-8">
                  {/* OCR Metrics */}
                  <div className="mb-8">
                    <h3 className="text-lg font-medium mb-4">OCR Metrics</h3>
                    <div className="bg-gray-50 rounded-lg p-4">
                      {run.ocr_metrics && run.ocr_metrics.length > 0 ? (
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-100">
                              <tr>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-300">Model</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-300">Text Length</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-300">Confidence Score</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-300">Duration</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-300">Timestamp</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Extracted Text</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {run.ocr_metrics.map((ocrMetric, index) => (
                                <React.Fragment key={index}>
                                  <tr className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                    <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-900 border-r border-gray-300">
                                      <span className={`${modelBadgeColor(ocrMetric.model)} text-xs px-2 py-1 rounded`}>
                                        {ocrMetric.model}
                                      </span>
                                    </td>
                                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700 border-r border-gray-300">
                                      {ocrMetric.text_length || 0}
                                    </td>
                                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700 border-r border-gray-300">
                                      <span className={`px-2 py-1 text-xs rounded ${ocrMetric.confidence_score >= 0.9 ? 'bg-green-100 text-green-800' :
                                        ocrMetric.confidence_score >= 0.7 ? 'bg-yellow-100 text-yellow-800' :
                                          'bg-red-100 text-red-800'
                                        }`}>
                                        {safeToFixed(ocrMetric.confidence_score * 100, 1)}%
                                      </span>
                                    </td>
                                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700 border-r border-gray-300">
                                      {safeToFixed(ocrMetric.duration, 2, "0")}s
                                    </td>
                                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700 border-r border-gray-300">
                                      {ocrMetric.timestamp ? new Date(ocrMetric.timestamp * 1000).toLocaleString() : 'N/A'}
                                    </td>
                                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700">
                                      {ocrMetric.text || ocrMetric.extracted_text ? (
                                        <button
                                          onClick={() => toggleOcrText(index)}
                                          className="text-blue-600 hover:text-blue-800 underline text-xs font-medium"
                                        >
                                          View Text
                                        </button>
                                      ) : (
                                        <span className="text-gray-400 text-xs">No text</span>
                                      )}
                                    </td>
                                  </tr>
                                  {(ocrTextOpenMap.get(index) || false) && (ocrMetric.text || ocrMetric.extracted_text) && (
                                    <tr className="bg-blue-50">
                                      <td colSpan="6" className="px-4 py-3">
                                        <div className="space-y-2">
                                          <p className="text-sm font-medium text-gray-900">Extracted Text:</p>
                                          <div className="bg-white p-3 rounded border border-gray-200 max-h-96 overflow-y-auto">
                                            <pre className="whitespace-pre-wrap break-words font-mono text-xs text-gray-700" style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                                              {ocrMetric.text || ocrMetric.extracted_text}
                                            </pre>
                                          </div>
                                        </div>
                                      </td>
                                    </tr>
                                  )}
                                </React.Fragment>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="text-center py-8">
                          <p className="text-gray-500 text-sm">No OCR metrics available for this run</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* LLM Metrics */}
                  <div className="mb-8">
                    <h3 className="text-lg font-medium mb-4">LLM Metrics</h3>
                    <div className="bg-gray-50 rounded-lg p-4">
                      {run.llm_calls && run.llm_calls.length > 0 ? (
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-100">
                              <tr>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-300">Model</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-300">Total Tokens</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-300">Latency</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-300">Cost</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Governance</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {run.llm_calls.map((llmCall, index) => {
                                // Extract governance data from llm_call or default values
                                const governance = llmCall.governance || {};
                                const hasGovernance = Object.keys(governance).length > 0;

                                // Governance fields that might exist
                                const complianceStatus = governance.compliance_status ||
                                  llmCall.compliance_status ||
                                  (llmCall.pii_detected ? 'Non-Compliant' : 'Compliant');
                                const piiDetected = governance.pii_detected ?? llmCall.pii_detected ?? false;
                                const toxicityScore = governance.toxicity_score ?? llmCall.toxicity_score ?? 0;
                                const biasScore = governance.bias_score ?? llmCall.bias_score ?? 0;
                                const riskLevel = governance.risk_level || llmCall.risk_level || 'Low';

                                return (
                                  <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                    <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-900 border-r border-gray-300">
                                      <span className={`${modelBadgeColor(llmCall.model)} text-xs px-2 py-1 rounded`}>
                                        {llmCall.model || 'Unknown'}
                                      </span>
                                    </td>
                                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700 border-r border-gray-300">
                                      {llmCall.total_tokens || 0}
                                    </td>
                                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700 border-r border-gray-300">
                                      {safeToFixed(llmCall.latency_ms, 2, "0")}ms
                                    </td>
                                    <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-700 border-r border-gray-300">
                                      ${safeToFixed(llmCall.cost, 5, "0.00000")}
                                    </td>
                                    <td className="px-4 py-2 text-sm text-gray-700">
                                      <div className="space-y-1">
                                        <div className="flex items-center justify-between">
                                          <span className="text-xs text-gray-600">Status:</span>
                                          <span className={`px-2 py-0.5 text-xs rounded ${complianceStatus === 'Compliant' || complianceStatus === 'compliant'
                                            ? 'bg-green-100 text-green-800'
                                            : complianceStatus === 'Non-Compliant' || complianceStatus === 'non-compliant'
                                              ? 'bg-red-100 text-red-800'
                                              : 'bg-yellow-100 text-yellow-800'
                                            }`}>
                                            {complianceStatus}
                                          </span>
                                        </div>
                                        <div className="flex items-center justify-between">
                                          <span className="text-xs text-gray-600">PII Detected:</span>
                                          <span className={`px-2 py-0.5 text-xs rounded ${piiDetected ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                                            }`}>
                                            {piiDetected ? 'Yes' : 'No'}
                                          </span>
                                        </div>
                                        <div className="flex items-center justify-between">
                                          <span className="text-xs text-gray-600">Risk Level:</span>
                                          <span className={`px-2 py-0.5 text-xs rounded ${riskLevel === 'High' || riskLevel === 'high'
                                            ? 'bg-red-100 text-red-800'
                                            : riskLevel === 'Medium' || riskLevel === 'medium'
                                              ? 'bg-yellow-100 text-yellow-800'
                                              : 'bg-green-100 text-green-800'
                                            }`}>
                                            {riskLevel}
                                          </span>
                                        </div>
                                        {toxicityScore > 0 && (
                                          <div className="flex items-center justify-between">
                                            <span className="text-xs text-gray-600">Toxicity:</span>
                                            <span className={`px-2 py-0.5 text-xs rounded ${toxicityScore > 0.5 ? 'bg-red-100 text-red-800' :
                                              toxicityScore > 0.3 ? 'bg-yellow-100 text-yellow-800' :
                                                'bg-green-100 text-green-800'
                                              }`}>
                                              {safeToFixed(toxicityScore * 100, 1)}%
                                            </span>
                                          </div>
                                        )}
                                        {biasScore > 0 && (
                                          <div className="flex items-center justify-between">
                                            <span className="text-xs text-gray-600">Bias:</span>
                                            <span className={`px-2 py-0.5 text-xs rounded ${biasScore > 0.5 ? 'bg-red-100 text-red-800' :
                                              biasScore > 0.3 ? 'bg-yellow-100 text-yellow-800' :
                                                'bg-green-100 text-green-800'
                                              }`}>
                                              {safeToFixed(biasScore * 100, 1)}%
                                            </span>
                                          </div>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="text-center py-8">
                          <p className="text-gray-500 text-sm">No LLM metrics available for this run</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* RAG Metrics */}
                  <div className="mb-8">
                    <h3 className="text-lg font-medium mb-4">RAG (Retrieval-Augmented Generation) Metrics</h3>
                    <div className="bg-gray-50 rounded-lg p-4">
                      {run.rag_metrics && run.rag_metrics.length > 0 ? (
                        <div className="space-y-4">
                          {(() => {
                            const processedRagMetrics = [];
                            const queryMap = new Map();
                            const executionMap = new Map();

                            run.rag_metrics.forEach(metric => {
                              if (metric.query) {
                                queryMap.set(metric.function_name, {
                                  function_name: metric.function_name,
                                  query: metric.query,
                                  query_length: metric.query_length,
                                  docs_count: metric.docs_count,
                                  result_count: metric.result_count,
                                  query_timestamp: metric.timestamp
                                });
                              } else if (metric.status !== undefined) {
                                executionMap.set(metric.function_name, {
                                  function_name: metric.function_name,
                                  status: metric.status,
                                  latency_ms: metric.latency_ms,
                                  cost: metric.cost,
                                  error: metric.error,
                                  operation_type: metric.operation_type,
                                  execution_timestamp: metric.timestamp
                                });
                              }
                            });

                            queryMap.forEach((queryData, functionName) => {
                              const executionData = executionMap.get(functionName);
                              if (executionData) {
                                processedRagMetrics.push({ ...queryData, ...executionData });
                              } else {
                                processedRagMetrics.push(queryData);
                              }
                            });

                            executionMap.forEach((executionData, functionName) => {
                              if (!queryMap.has(functionName)) {
                                processedRagMetrics.push(executionData);
                              }
                            });

                            return processedRagMetrics.map((ragMetric, index) => (
                              <div key={index} className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
                                <div className="flex items-center justify-between mb-4">
                                  <span className="bg-blue-100 text-blue-800 text-sm px-3 py-1 rounded-full font-medium">
                                    {ragMetric.function_name || 'N/A'}
                                  </span>
                                  <span className="text-xs text-gray-500">
                                    {ragMetric.execution_timestamp ? new Date(ragMetric.execution_timestamp * 1000).toLocaleString() :
                                      ragMetric.query_timestamp ? new Date(ragMetric.query_timestamp * 1000).toLocaleString() : 'N/A'}
                                  </span>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  <div className="bg-blue-50 rounded-lg p-3">
                                    <h4 className="text-sm font-semibold text-blue-800 mb-3">Query Details</h4>
                                    <div className="space-y-2 text-sm">
                                      <div className="flex justify-between">
                                        <span className="text-gray-600">Query:</span>
                                        <span className="font-medium text-gray-900 max-w-xs truncate">{ragMetric.query || 'N/A'}</span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-gray-600">Query Length:</span>
                                        <span className="font-medium text-gray-900">{ragMetric.query_length || 0}</span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-gray-600">Docs Count:</span>
                                        <span className="font-medium text-gray-900">{ragMetric.docs_count || 0}</span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-gray-600">Result Count:</span>
                                        <span className="font-medium text-gray-900">{ragMetric.result_count || 0}</span>
                                      </div>
                                    </div>
                                  </div>

                                  <div className="bg-green-50 rounded-lg p-3">
                                    <h4 className="text-sm font-semibold text-green-800 mb-3">Operation Details</h4>
                                    <div className="space-y-2 text-sm">
                                      <div className="flex justify-between">
                                        <span className="text-gray-600">Status:</span>
                                        <span className={`px-2 py-1 text-xs rounded font-medium ${ragMetric.status === 'success' ? 'bg-green-100 text-green-800' :
                                          ragMetric.status === 'error' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'
                                          }`}>
                                          {(ragMetric.status || 'N/A').toUpperCase()}
                                        </span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-gray-600">Operation Type:</span>
                                        <span className="font-medium text-gray-900">{ragMetric.operation_type || 'N/A'}</span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-gray-600">Latency:</span>
                                        <span className="font-medium text-gray-900">
                                          {ragMetric.latency_ms ? `${safeToFixed(ragMetric.latency_ms, 3, "0.000")}ms` : 'N/A'}
                                        </span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-gray-600">Cost:</span>
                                        <span className="font-medium text-gray-900">${safeToFixed(ragMetric.cost, 5, "0.00000")}</span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ));
                          })()}
                        </div>
                      ) : (
                        <div className="text-center py-8">
                          <p className="text-gray-500 text-sm">No RAG metrics available for this run</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Embedding Metrics */}
                  <div className="mb-8">
                    <h3 className="text-lg font-medium mb-4">Embedding Metrics</h3>
                    <div className="bg-gray-50 rounded-lg p-4">
                      {run.embedding_metrics && run.embedding_metrics.length > 0 ? (
                        <div className="space-y-4">
                          {(() => {
                            const processedEmbeddingMetrics = [];
                            const inputMap = new Map();
                            const executionMap = new Map();

                            run.embedding_metrics.forEach(metric => {
                              if (metric.input_length !== undefined) {
                                inputMap.set(metric.function_name, {
                                  function_name: metric.function_name,
                                  input_length: metric.input_length,
                                  dimensions: metric.dimensions,
                                  quality_score: metric.quality_score,
                                  input_timestamp: metric.timestamp
                                });
                              } else if (metric.status !== undefined) {
                                executionMap.set(metric.function_name, {
                                  function_name: metric.function_name,
                                  status: metric.status,
                                  latency_ms: metric.latency_ms,
                                  cost: metric.cost,
                                  error: metric.error,
                                  operation_type: metric.operation_type,
                                  execution_timestamp: metric.timestamp
                                });
                              }
                            });

                            inputMap.forEach((inputData, functionName) => {
                              const executionData = executionMap.get(functionName);
                              if (executionData) {
                                processedEmbeddingMetrics.push({ ...inputData, ...executionData });
                              } else {
                                processedEmbeddingMetrics.push(inputData);
                              }
                            });

                            executionMap.forEach((executionData, functionName) => {
                              if (!inputMap.has(functionName)) {
                                processedEmbeddingMetrics.push(executionData);
                              }
                            });

                            return processedEmbeddingMetrics.map((embeddingMetric, index) => (
                              <div key={index} className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
                                <div className="flex items-center justify-between mb-4">
                                  <span className="bg-purple-100 text-purple-800 text-sm px-3 py-1 rounded-full font-medium">
                                    {embeddingMetric.function_name || 'N/A'}
                                  </span>
                                  <span className="text-xs text-gray-500">
                                    {embeddingMetric.execution_timestamp ? new Date(embeddingMetric.execution_timestamp * 1000).toLocaleString() :
                                      embeddingMetric.input_timestamp ? new Date(embeddingMetric.input_timestamp * 1000).toLocaleString() : 'N/A'}
                                  </span>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  <div className="bg-purple-50 rounded-lg p-3">
                                    <h4 className="text-sm font-semibold text-purple-800 mb-3">Input Details</h4>
                                    <div className="space-y-2 text-sm">
                                      <div className="flex justify-between">
                                        <span className="text-gray-600">Input Length:</span>
                                        <span className="font-medium text-gray-900">{embeddingMetric.input_length || 0}</span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-gray-600">Dimensions:</span>
                                        <span className="font-medium text-gray-900">{embeddingMetric.dimensions || 0}</span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-gray-600">Quality Score:</span>
                                        <span className={`px-2 py-1 text-xs rounded font-medium ${embeddingMetric.quality_score >= 0.8 ? 'bg-green-100 text-green-800' :
                                          embeddingMetric.quality_score >= 0.6 ? 'bg-yellow-100 text-yellow-800' :
                                            'bg-red-100 text-red-800'
                                          }`}>
                                          {safeToFixed(embeddingMetric.quality_score, 2, "0.00")}
                                        </span>
                                      </div>
                                    </div>
                                  </div>

                                  <div className="bg-green-50 rounded-lg p-3">
                                    <h4 className="text-sm font-semibold text-green-800 mb-3">Operation Details</h4>
                                    <div className="space-y-2 text-sm">
                                      <div className="flex justify-between">
                                        <span className="text-gray-600">Status:</span>
                                        <span className={`px-2 py-1 text-xs rounded font-medium ${embeddingMetric.status === 'success' ? 'bg-green-100 text-green-800' :
                                          embeddingMetric.status === 'error' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'
                                          }`}>
                                          {(embeddingMetric.status || 'N/A').toUpperCase()}
                                        </span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-gray-600">Operation Type:</span>
                                        <span className="font-medium text-gray-900">{embeddingMetric.operation_type || 'N/A'}</span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-gray-600">Latency:</span>
                                        <span className="font-medium text-gray-900">
                                          {embeddingMetric.latency_ms ? `${safeToFixed(embeddingMetric.latency_ms, 3, "0.000")}ms` : 'N/A'}
                                        </span>
                                      </div>
                                      <div className="flex justify-between">
                                        <span className="text-gray-600">Cost:</span>
                                        <span className="font-medium text-gray-900">${safeToFixed(embeddingMetric.cost, 5, "0.00000")}</span>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ));
                          })()}
                        </div>
                      ) : (
                        <div className="text-center py-8">
                          <p className="text-gray-500 text-sm">No embedding metrics available for this run</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Custom Metrics */}
                  <div className="mb-8">
                    <h3 className="text-lg font-medium mb-4">Custom Metrics</h3>
                    <div className="bg-gray-50 rounded-lg p-4">
                      {Object.keys(customLogs).length > 0 ? (
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-100">
                              <tr>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-300">Key</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Value</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {Object.entries(customLogs).map(([key, value], index) => (
                                <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                  <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-gray-900 border-r border-gray-300">{key}</td>
                                  <td className="px-4 py-2 text-sm text-gray-700">
                                    {typeof value === 'object' ? (
                                      <pre className="text-xs whitespace-pre-wrap">{JSON.stringify(value, null, 2)}</pre>
                                    ) : (
                                      String(value)
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="text-center py-8">
                          <p className="text-gray-500 text-sm">No custom metrics available for this run</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Traces Tab */}
              {activeModalTab === 'Traces' && (
                <div className="mb-8">
                  <h3 className="text-lg font-medium mb-4">Traces</h3>
                  <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                    {traceIds && traceIds.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Trace ID</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Root Span</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Duration</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">LLM Calls</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Framework</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {traceIds.map((traceId, index) => {
                              const traceData = tracesData[traceId];
                              const isExpanded = expandedTraces.has(traceId);
                              const isLoading = loadingTraces.has(traceId);
                              const trace = traceData?.trace;
                              const rootSpan = trace?.root_span;
                              const parentSpansMap = traceData?.parentSpansMap || {};
                              const rootSpanId = traceData?.rootSpanId;

                              const parentSpans = trace?.parent_spans || {};
                              const rootParentSpans = parentSpans[rootSpanId] || [];

                              const actualRootParentSpans = rootParentSpans.length > 0
                                ? rootParentSpans
                                : (parentSpansMap[rootSpanId] || []).map(s => s.span_id);

                              return (
                                <React.Fragment key={traceId}>
                                  <tr className="hover:bg-gray-50">
                                    <td className="px-4 py-3 whitespace-nowrap">
                                      <div className="flex items-center gap-2">
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            toggleTrace(traceId);
                                          }}
                                          className="text-blue-600 hover:text-blue-800 flex items-center"
                                        >
                                          {isExpanded ? <FaChevronDown className="text-xs" /> : <FiChevronRight className="text-xs" />}
                                        </button>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleTraceClick(traceId);
                                          }}
                                          className="text-blue-600 hover:text-blue-800 font-mono text-sm underline truncate max-w-xs"
                                        >
                                          {traceId}
                                        </button>
                                      </div>
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap">
                                      {rootSpan ? (
                                        <span className="text-sm text-gray-900">{rootSpan.name || rootSpan.span_id}</span>
                                      ) : (
                                        <span className="text-sm text-gray-400">-</span>
                                      )}
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                      {trace ? `${safeToFixed(trace.total_duration_seconds, 2)}s` : '-'}
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                      {trace ? trace.total_llm_calls || 0 : '-'}
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                                      {trace ? trace.framework || '-' : '-'}
                                    </td>
                                  </tr>

                                  {isLoading && (
                                    <tr>
                                      <td colSpan="5" className="px-4 py-3 text-center text-sm text-gray-500">
                                        Loading trace data...
                                      </td>
                                    </tr>
                                  )}

                                  {isExpanded && traceData && actualRootParentSpans.length > 0 && (
                                    <>
                                      {actualRootParentSpans.map((parentSpanId) => {
                                        const parentSpanKey = `${traceId}:${parentSpanId}`;
                                        const isParentExpanded = expandedParentSpans.has(parentSpanKey);

                                        let childSpans = traceData.parentToChildrenMap[parentSpanId] || [];
                                        if (childSpans.length === 0) {
                                          childSpans = parentSpansMap[parentSpanId] || [];
                                        }

                                        const parentSpanDetails = traceData.spans.find(s => s.span_id === parentSpanId);

                                        return (
                                          <React.Fragment key={parentSpanId}>
                                            <tr className="bg-gray-50">
                                              <td className="px-4 py-2">
                                                <div className="flex items-center gap-2 pl-6">
                                                  <button
                                                    onClick={() => toggleParentSpan(traceId, parentSpanId)}
                                                    className="text-blue-600 hover:text-blue-800 font-mono text-xs flex items-center gap-1 mr-2"
                                                  >
                                                    {isParentExpanded ? <FaChevronDown className="text-xs" /> : <FiChevronRight className="text-xs" />}
                                                  </button>
                                                  <span className="truncate max-w-xs font-mono text-xs text-gray-700">
                                                    {parentSpanId}
                                                  </span>
                                                </div>
                                              </td>
                                              <td className="px-4 py-2">
                                                {parentSpanDetails ? (
                                                  <span className="text-sm text-gray-900">{parentSpanDetails.name || parentSpanId}</span>
                                                ) : (
                                                  <span className="text-sm text-gray-400">-</span>
                                                )}
                                              </td>
                                              <td className="px-4 py-2 text-sm text-gray-900">
                                                {parentSpanDetails ? `${safeToFixed(parentSpanDetails.duration_seconds, 3)}s` : '-'}
                                              </td>
                                              <td className="px-4 py-2 text-sm text-gray-900">-</td>
                                              <td className="px-4 py-2 text-sm text-gray-900">-</td>
                                            </tr>

                                            {isParentExpanded && childSpans.map((childSpan) => (
                                              <tr key={childSpan.span_id} className="bg-gray-100">
                                                <td className="px-4 py-2">
                                                  <div className="flex items-center gap-2 pl-12">
                                                    <span className="font-mono text-xs text-gray-700">
                                                      {childSpan.span_id}
                                                    </span>
                                                  </div>
                                                </td>
                                                <td className="px-4 py-2">
                                                  <span className="text-sm text-gray-900">{childSpan.name || childSpan.span_id}</span>
                                                </td>
                                                <td className="px-4 py-2 text-sm text-gray-900">
                                                  {childSpan.duration_seconds ? `${safeToFixed(childSpan.duration_seconds, 3)}s` : '-'}
                                                </td>
                                                <td className="px-4 py-2 text-sm text-gray-900">-</td>
                                                <td className="px-4 py-2 text-sm text-gray-900">-</td>
                                              </tr>
                                            ))}
                                          </React.Fragment>
                                        );
                                      })}
                                    </>
                                  )}
                                </React.Fragment>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <p className="text-gray-500 text-sm mb-2">No trace data available for this run</p>
                        <p className="text-gray-400 text-xs">Create agents to display traces</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Logs Tab */}
              {activeModalTab === 'Logs' && (
                <div>
                  <h3 className="text-lg font-medium mb-4">Errors & Logs</h3>
                  <div className="bg-gray-50 rounded-lg p-4 max-h-60 overflow-y-auto">
                    {logs.length === 0 ? (
                      <p className="text-gray-500 text-sm">No logs available for this run</p>
                    ) : (
                      logs.map((log, index) => (
                        <div key={index} className="text-sm mb-1">
                          <span className={`font-mono ${log.type === 'error' ? 'text-red-600' : 'text-gray-600'}`}>
                            [{log.type?.toUpperCase() || 'INFO'}]
                          </span>
                          <span className="ml-1">{new Date(log.timestamp).toLocaleTimeString()}:</span>
                          <span className="ml-1">{log.message}</span>
                          <span className="text-gray-500 ml-1">({log.source})</span>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}

              {/* System Tab */}
              {activeModalTab === 'System' && run.system_metrics && typeof run.system_metrics === 'object' && (
                <div className="mb-8">
                  <h3 className="text-lg font-medium mb-4">System Metrics</h3>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                      {/* OTEL Metrics */}
                      {run.system_metrics['otel::system.cpu.utilization'] !== undefined && (
                        <div className="bg-white p-3 rounded-md">
                          <p className="text-gray-600 font-medium">System CPU</p>
                          <p className="text-lg font-semibold text-blue-600">
                            {safeToFixed(run.system_metrics['otel::system.cpu.utilization'] * 100, 1)}%
                          </p>
                        </div>
                      )}

                      {run.system_metrics['otel::process.cpu.utilization'] !== undefined && (
                        <div className="bg-white p-3 rounded-md">
                          <p className="text-gray-600 font-medium">Process CPU</p>
                          <p className="text-lg font-semibold text-blue-600">
                            {safeToFixed(run.system_metrics['otel::process.cpu.utilization'] * 100, 1)}%
                          </p>
                        </div>
                      )}

                      {run.system_metrics['otel::system.memory.utilization'] !== undefined && (
                        <div className="bg-white p-3 rounded-md">
                          <p className="text-gray-600 font-medium">Memory Usage</p>
                          <p className="text-lg font-semibold text-green-600">
                            {safeToFixed(run.system_metrics['otel::system.memory.utilization'] * 100, 1)}%
                          </p>
                        </div>
                      )}

                      {run.system_metrics['otel::system.memory.usage'] && (
                        <div className="bg-white p-3 rounded-md">
                          <p className="text-gray-600 font-medium">Memory Used</p>
                          <p className="text-lg font-semibold text-green-600">
                            {safeToFixed(run.system_metrics['otel::system.memory.usage'] / 1024 / 1024 / 1024, 2)} GB
                          </p>
                        </div>
                      )}

                      {/* Disk Metrics */}
                      {run.system_metrics.disk_total_space_bytes && (
                        <div className="bg-white p-3 rounded-md">
                          <p className="text-gray-600 font-medium">Disk Total</p>
                          <p className="text-lg font-semibold text-yellow-600">
                            {safeToFixed(run.system_metrics.disk_total_space_bytes / 1024 / 1024 / 1024, 1)} GB
                          </p>
                        </div>
                      )}

                      {run.system_metrics.disk_used_space_bytes && (
                        <div className="bg-white p-3 rounded-md">
                          <p className="text-gray-600 font-medium">Disk Used</p>
                          <p className="text-lg font-semibold text-yellow-600">
                            {safeToFixed(run.system_metrics.disk_used_space_bytes / 1024 / 1024 / 1024, 1)} GB
                          </p>
                        </div>
                      )}

                      {run.system_metrics.disk_free_space_bytes && (
                        <div className="bg-white p-3 rounded-md">
                          <p className="text-gray-600 font-medium">Disk Free</p>
                          <p className="text-lg font-semibold text-yellow-600">
                            {safeToFixed(run.system_metrics.disk_free_space_bytes / 1024 / 1024 / 1024, 1)} GB
                          </p>
                        </div>
                      )}

                      {/* Process Metrics */}
                      {run.system_metrics.process_memory_in_use && (
                        <div className="bg-white p-3 rounded-md">
                          <p className="text-gray-600 font-medium">Process Memory</p>
                          <p className="text-lg font-semibold text-purple-600">
                            {safeToFixed(run.system_metrics.process_memory_in_use / 1024 / 1024, 1)} MB
                          </p>
                        </div>
                      )}

                      {run.system_metrics.process_memory_available && (
                        <div className="bg-white p-3 rounded-md">
                          <p className="text-gray-600 font-medium">Memory Available</p>
                          <p className="text-lg font-semibold text-purple-600">
                            {safeToFixed(run.system_metrics.process_memory_available / 1024 / 1024 / 1024, 1)} GB
                          </p>
                        </div>
                      )}

                      {run.system_metrics.process_cpu_threads_in_use && (
                        <div className="bg-white p-3 rounded-md">
                          <p className="text-gray-600 font-medium">CPU Threads</p>
                          <p className="text-lg font-semibold text-blue-600">
                            {run.system_metrics.process_cpu_threads_in_use}
                          </p>
                        </div>
                      )}

                      {/* Network Metrics */}
                      {run.system_metrics['otel::system.network.io'] && (
                        <div className="bg-white p-3 rounded-md">
                          <p className="text-gray-600 font-medium">Network I/O</p>
                          <p className="text-lg font-semibold text-indigo-600">
                            {run.system_metrics['otel::system.network.io']} bytes
                          </p>
                        </div>
                      )}

                      {run.system_metrics['otel::system.network.packets'] && (
                        <div className="bg-white p-3 rounded-md">
                          <p className="text-gray-600 font-medium">Network Packets</p>
                          <p className="text-lg font-semibold text-indigo-600">
                            {run.system_metrics['otel::system.network.packets']}
                          </p>
                        </div>
                      )}

                      {/* Process Status */}
                      {run.system_metrics.process_status && (
                        <div className="bg-white p-3 rounded-md">
                          <p className="text-gray-600 font-medium">Process Status</p>
                          <p className="text-lg font-semibold text-gray-700">
                            {run.system_metrics.process_status}
                          </p>
                        </div>
                      )}

                      {/* OTEL Token Metrics */}
                      {run.system_metrics['otel::tokens_total'] && (
                        <div className="bg-white p-3 rounded-md">
                          <p className="text-gray-600 font-medium">Total Tokens</p>
                          <p className="text-lg font-semibold text-gray-700">
                            {run.system_metrics['otel::tokens_total']}
                          </p>
                        </div>
                      )}

                      {/* LLM Latency */}
                      {run.system_metrics['otel::llm_latency::sum'] && (
                        <div className="bg-white p-3 rounded-md">
                          <p className="text-gray-600 font-medium">LLM Latency</p>
                          <p className="text-lg font-semibold text-gray-700">
                            {safeToFixed(run.system_metrics['otel::llm_latency::sum'], 0)} ms
                          </p>
                        </div>
                      )}

                      {/* Collection Duration */}
                      {run.system_metrics.collection_duration_seconds && (
                        <div className="bg-white p-3 rounded-md">
                          <p className="text-gray-600 font-medium">Collection Duration</p>
                          <p className="text-lg font-semibold text-gray-700">
                            {safeToFixed(run.system_metrics.collection_duration_seconds, 3)}s
                          </p>
                        </div>
                      )}

                      {/* Timestamp */}
                      {run.system_metrics.metrics_collected_timestamp && (
                        <div className="bg-white p-3 rounded-md">
                          <p className="text-gray-600 font-medium">Collected At</p>
                          <p className="text-sm font-medium text-gray-700">
                            {new Date(run.system_metrics.metrics_collected_timestamp * 1000).toLocaleTimeString()}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
              {activeModalTab === 'System' && (!run.system_metrics || typeof run.system_metrics !== 'object') && (
                <div className="text-center py-8">
                  <p className="text-gray-500 text-sm">No system metrics available for this run</p>
                </div>
              )}
            </div>
          </div>

          {/* Trace Details Modal (LangSmith-style) */}
          {showTraceModal && <TraceDetailsModal trace={selectedTrace} onClose={closeTraceModal} />}
        </div >
      );
    };

    // Get unique models used in this project
    const getProjectModels = () => {
      // First, try to use models_used from project data for consistency
      if (project?.models_used && project.models_used.length > 0) {
        console.log('Using models from project.models_used:', project.models_used);
        return project.models_used;
      }

      // Fallback: Extract from runs
      const models = new Set();
      allRuns.forEach(run => {
        if (run.llm_calls && run.llm_calls.length > 0) {
          // Extract models from ALL LLM calls in each run
          run.llm_calls.forEach(call => {
            const model = call.model;
            if (model) {
              models.add(model);
            }
          });
        }
      });

      // Debug: Log the models found
      console.log('Models found in project runs:', Array.from(models));
      console.log('Project data models_used:', project?.models_used);

      return Array.from(models);
    };

    const projectModels = getProjectModels();

    return (
      <div className="space-y-6">
        {/* Project Header */}
        <div className="bg-white -m-6 p-6 shadow-sm">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                {project?.name || project?.project || `Project ${projectId}`}
              </h1>
              <p className="text-gray-600 text-sm mb-3">
                Project details, analytics, and run history
              </p>
              <div className="flex items-center gap-2 mb-3">
                <span className="text-sm text-gray-500">Models used:</span>
                <div className="flex gap-1">
                  {projectModels.length > 0 ? (
                    projectModels.map((model, idx) => (
                      <span key={idx} className={`${modelBadgeColor(model)} text-xs px-2 py-1 rounded`}>
                        {model}
                      </span>
                    ))
                  ) : (
                    <span className="text-xs text-gray-500">No models found</span>
                  )}
                </div>
              </div>
              <div className="bg-gray-100 rounded-lg p-4 flex flex-wrap gap-6">
                <div className="flex items-center">
                  <div className="w-5 h-5 bg-gray-200 rounded mr-2"></div>
                  <span className="text-gray-600 text-sm">Created: {project?.created_at ? new Date(project.created_at).toLocaleDateString() : 'Unknown'}</span>
                </div>
                <div className="flex items-center">
                  <div className="w-5 h-5 bg-gray-200 rounded mr-2"></div>
                  <span className="text-gray-600 text-sm">Total Runs: {allRuns.length}</span>
                </div>
              </div>
            </div>
            <Link
              to="/"
              className="bg-blue-600 text-white px-4 py-2 rounded-md flex items-center hover:bg-blue-700 transition-colors"
            >
              <FaArrowLeft className="mr-2" />
              Back to Dashboard
            </Link>
          </div>
        </div>

        {/* Daily Token Usage and Cost Section - Moved to top */}
        <div className="bg-white rounded-lg p-6 shadow-sm">
          <div className="mb-4">
            <h2 className="text-lg font-semibold">Daily Token Usage & Cost</h2>
          </div>

          {/* Summary metrics */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="text-sm text-gray-500 mb-1">Total Token Usage</div>
              <div className="text-xl font-semibold">
                {project?.token_usage ||
                  allRuns.reduce((sum, run) => sum + (run.llm_calls?.reduce((callSum, call) => callSum + (call.total_tokens || 0), 0) || 0), 0)}
              </div>
            </div>
            <div className="bg-green-50 rounded-lg p-4">
              <div className="text-sm text-gray-500 mb-1">Total Cost</div>
              <div className="text-xl font-semibold">
                ${(() => {
                  try {
                    // Try project costs first
                    const projectCosts = Object.values(project?.costs || {}).reduce((a, b) => a + (parseFloat(b) || 0), 0);
                    if (projectCosts > 0) {
                      return safeToFixed(projectCosts, 5);
                    }

                    // Fallback to calculating from runs
                    const runsCost = allRuns.reduce((sum, run) => {
                      return sum + (run.llm_calls?.reduce((callSum, call) => {
                        const cost = parseFloat(call.cost) || 0;
                        return callSum + cost;
                      }, 0) || 0);
                    }, 0);

                    return safeToFixed(runsCost, 5);
                  } catch (error) {
                    console.warn('Error calculating total cost:', error);
                    return '0.00000';
                  }
                })()}
              </div>
            </div>
            <div className="bg-purple-50 rounded-lg p-4">
              <div className="text-sm text-gray-500 mb-1">Success Rate</div>
              <div className="text-xl font-semibold">
                {(() => {
                  try {
                    if (project?.success_rate !== undefined && project.success_rate !== null) {
                      return safeToFixed(parseFloat(project.success_rate) * 100, 1);
                    }

                    if (allRuns.length > 0) {
                      const successCount = allRuns.filter(run => run.status === 'success').length;
                      return Math.round((successCount / allRuns.length) * 100);
                    }

                    return 0;
                  } catch (error) {
                    console.warn('Error calculating success rate:', error);
                    return 0;
                  }
                })()}%
              </div>
            </div>
            <div className="bg-amber-50 rounded-lg p-4">
              <div className="text-sm text-gray-500 mb-1">Avg. Latency</div>
              <div className="text-xl font-semibold">
                {(() => {
                  try {
                    const allLatencies = [];
                    allRuns.forEach(run => {
                      if (run.llm_calls?.length > 0) {
                        run.llm_calls.forEach(call => {
                          const latency = parseFloat(call.latency_ms);
                          if (latency && !isNaN(latency)) {
                            allLatencies.push(latency);
                          }
                        });
                      }
                    });

                    if (allLatencies.length === 0) return 'N/A';

                    const avgLatency = allLatencies.reduce((sum, lat) => sum + lat, 0) / allLatencies.length;
                    return `${safeToFixed(avgLatency / 1000, 2)}s`;
                  } catch (error) {
                    console.warn('Error calculating average latency:', error);
                    return 'N/A';
                  }
                })()}
              </div>
            </div>
          </div>


        </div>

        {/* Token Usage & Performance - Now below the metrics */}
        <div className="flex gap-6">
          <div className="bg-white rounded-lg p-6 shadow-sm flex-1">
            <h2 className="text-lg font-semibold mb-4">Token Usage & Performance</h2>

            {/* Tabs */}
            <div className="flex border-b border-gray-200 mb-4">
              <button
                className={`py-2 px-4 ${activeTab === 'Token Usage' ? 'border-b-2 border-blue-500 text-blue-500' : 'text-gray-500'}`}
                onClick={() => handleTabChange('Token Usage')}
              >
                Token Usage
              </button>
              <button
                className={`py-2 px-4 ${activeTab === 'Latency' ? 'border-b-2 border-blue-500 text-blue-500' : 'text-gray-500'}`}
                onClick={() => handleTabChange('Latency')}
              >
                Latency
              </button>
              <button
                className={`py-2 px-4 ${activeTab === 'Error Rate' ? 'border-b-2 border-blue-500 text-blue-500' : 'text-gray-500'}`}
                onClick={() => handleTabChange('Error Rate')}
              >
                Error Rate
              </button>
            </div>

            {/* Dynamic chart based on active tab */}
            {activeTab === 'Token Usage' && <TokenUsageChart />}
            {activeTab === 'Latency' && <LatencyChart />}
            {activeTab === 'Error Rate' && <ErrorRateChart />}
          </div>
        </div>

        {/* Daily Cost Breakdown Chart */}
        <div className="bg-white rounded-lg p-6 shadow-sm">
          <h3 className="text-lg font-semibold mb-4">Daily Cost Breakdown</h3>
          <div className="bg-gray-50 rounded-lg p-4">
            <DailyCostBreakdownChart />
            <div className="flex justify-between items-center mt-2 px-4">
              <div className="flex items-center">
                <div className="w-3 h-3 rounded-sm mr-1.5" style={{ backgroundColor: '#E0EDFF' }}></div>
                <span className="text-xs text-gray-600">Token Usage</span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 rounded-sm mr-1.5" style={{ backgroundColor: '#10B981' }}></div>
                <span className="text-xs text-gray-600">Cost ($)</span>
              </div>
            </div>
          </div>
        </div>

        {/* Advanced Analytics with all three charts */}
        <div className="bg-white rounded-lg p-6 shadow-sm mb-6 border-2 border-blue-200">
          <AdvancedAnalytics />
        </div>

        {/* Run History Section */}
        <div className="bg-white rounded-lg p-6 shadow-sm">
          <h2 className="text-lg font-semibold mb-4 font-manrope">Run History</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 font-['Manrope']">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-bold text-gray-800 uppercase tracking-wider font-['Manrope']">Run ID</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-bold text-gray-800 uppercase tracking-wider font-['Manrope']">Start Time</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-bold text-gray-800 uppercase tracking-wider font-['Manrope']">Duration</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-bold text-gray-800 uppercase tracking-wider font-['Manrope']">Tokens</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-bold text-gray-800 uppercase tracking-wider font-['Manrope']">Cost</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-bold text-gray-800 uppercase tracking-wider font-['Manrope']">Memory</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-bold text-gray-800 uppercase tracking-wider font-['Manrope']">Status</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200 font-['Manrope']">
                {currentRuns.map((run, index) => {
                  // PRIORITY 1: Use common run-level fields (token_usage, costs, cost) - these are the source of truth
                  let totalTokens = safeNumber(run.token_usage, 0);
                  let totalCost = 0;
                  
                  // Use run.costs dict if available (model->cost mapping)
                  if (run.costs && typeof run.costs === 'object' && Object.keys(run.costs).length > 0) {
                    totalCost = Object.values(run.costs).reduce((sum, cost) => sum + safeNumber(cost), 0);
                  }
                  // Fallback to run.cost (single total cost)
                  else if (run.cost !== undefined && run.cost !== null) {
                    totalCost = safeNumber(run.cost);
                  }
                  
                  // PRIORITY 2: Fallback to calculating from llm_calls (for old data format compatibility)
                  if (totalTokens === 0) {
                    totalTokens = run.llm_calls?.reduce((sum, call) => sum + safeNumber(call.total_tokens), 0) || 0;
                  }
                  if (totalCost === 0) {
                    totalCost = run.llm_calls?.reduce((sum, call) => sum + safeNumber(call.cost), 0) || 0;
                  }

                  return (
                    <tr key={index} className="font-['Manrope']">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 font-['Manrope']">
                        <span
                          className="cursor-pointer hover:text-blue-600 hover:underline font-['Manrope']"
                          onClick={() => handleStatusClick(run)}
                        >
                          {run.run_id.substring(0, 8)}...
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-['Manrope']">
                        {new Date(run.start_time).toLocaleString('en-US', {
                          year: 'numeric',
                          month: 'numeric',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                          second: '2-digit'
                        })}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-['Manrope']">{safeToFixed(run.execution_duration, 2)}s</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-['Manrope']">{totalTokens}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-['Manrope']">${safeToFixed(totalCost, 5)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-['Manrope']">
                        {run.system_metrics ?
                          `${safeToFixed(run.system_metrics['otel::system.memory.utilization'] * 100, 1)}%` :
                          'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap font-['Manrope']">
                        <span
                          className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full font-['Manrope'] ${run.status === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}
                        >
                          {run.status}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="border-t border-gray-200 p-4 flex justify-between items-center mt-4 font-['Manrope']">
            <div className="text-sm text-gray-500 font-['Manrope']">
              Showing {indexOfFirstRun + 1}-{Math.min(indexOfLastRun, allRuns.length)} of {allRuns.length} runs
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={firstPage}
                disabled={currentPage === 1}
                className={`border border-gray-300 rounded-md p-1 font-['Manrope'] ${currentPage === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-100'}`}
              >
                <HiChevronDoubleLeft className="w-5 h-5" />
              </button>
              <button
                onClick={prevPage}
                disabled={currentPage === 1}
                className={`border border-gray-300 rounded-md p-1 font-['Manrope'] ${currentPage === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-100'}`}
              >
                <HiChevronLeft className="w-5 h-5" />
              </button>

              {/* Page numbers */}
              {[...Array(totalPages).keys()].map(number => (
                <button
                  key={number + 1}
                  onClick={() => paginate(number + 1)}
                  className={`border border-gray-300 rounded-md w-8 h-8 flex items-center justify-center font-['Manrope'] ${currentPage === number + 1 ? 'bg-blue-600 text-white' : 'text-gray-500 hover:bg-gray-100'
                    }`}
                >
                  {number + 1}
                </button>
              ))}

              <button
                onClick={nextPage}
                disabled={currentPage === totalPages}
                className={`border border-gray-300 rounded-md p-1 font-['Manrope'] ${currentPage === totalPages ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-100'}`}
              >
                <HiChevronRight className="w-5 h-5" />
              </button>
              <button
                onClick={lastPage}
                disabled={currentPage === totalPages}
                className={`border border-gray-300 rounded-md p-1 font-['Manrope'] ${currentPage === totalPages ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-100'}`}
              >
                <HiChevronDoubleRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Run Details Modal */}
        {showModal && selectedRun && <RunDetailsModal run={selectedRun} />}
      </div>
    );
  } catch (renderError) {
    console.error('Error rendering ProjectDetails:', renderError);
    return (
      <div className="space-y-6">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <img src="/images/svg/error-icon.svg" alt="Error" className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error rendering project details</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>An error occurred while rendering the project details.</p>
                <p className="mt-1 text-xs">Error: {renderError.message}</p>
              </div>
            </div>
          </div>
        </div>
        <Link to="/projects" className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700">
          ← Back to Projects
        </Link>
      </div>
    );
  }
};

export default ProjectDetails; 